"use client";

import React from "react";
import { ArrowRight, CircleCheck as CheckCircle2, FileCheck2, Footprints, Compass, Paintbrush, Ruler, ShieldCheck, TriangleAlert } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from "@/components/ui/table";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, LocalizedText } from "@/lib/i18n";
import DecisionFlow, { type DecisionStep } from "@/components/diagrams/DecisionFlow";
import DiagramFrame from "@/components/diagrams/DiagramFrame";
import CheckeredPlateDiagram from "@/components/diagrams/CheckeredPlateDiagram";
interface PatternProfile {
  id: string;
  title: LocalizedText;
  badge: LocalizedText;
  type: LocalizedText;
  to: string;
  desc: LocalizedText;
  bestFor: LocalizedText;
  thickness: LocalizedText;
  ribHeight: LocalizedText;
}
const patternProfiles: PatternProfile[] = [{
  id: "diamond",
  title: {
    en: "Diamond Checkered Plate",
    zh: "菱形花纹钢板",
    de: "Rauten-Riffelblech",
    es: "Chapa lagrimada de diamante",
    fr: "Tôle larmée motif losange",
    pt: "Chapa xadrez padrão diamante"
  },
  badge: {
    en: "Architectural & Safety",
    zh: "多向防滑",
    de: "Architektur & Sicherheit",
    es: "Arquitectura y seguridad",
    fr: "Architecture et sécurité",
    pt: "Arquitetura e segurança"
  },
  type: {
    en: "Rolled Raised Pattern",
    zh: "热轧凸纹型",
    de: "Warmgewalztes Riffelprofil",
    es: "Perfil laminado en relieve",
    fr: "Profil laminé en relief",
    pt: "Perfil laminado em relevo"
  },
  to: "/DiamondCheckeredPlatePage",
  desc: {
    en: "Raised diamond lug array providing balanced multidirectional grip with clean industrial visual appeal.",
    zh: "凸起菱形点状阵列，提供均衡的多向抗滑性能，兼具整洁的工业与装饰质感。",
    de: "Erhabene Rautennoppen für ausgewogene, multidirektionale Rutschhemmung mit klarer industrieller Ästhetik.",
    es: "Matriz de resaltes en forma de diamante que proporciona agarre multidireccional equilibrado y estética industrial limpia.",
    fr: "Réseau de losanges en relief offrant une adhérence multidirectionnelle équilibrée et un aspect industriel soigné.",
    pt: "Matriz de relevos em diamante proporcionando aderência multidirecional equilibrada com apelo visual industrial limpo."
  },
  bestFor: {
    en: "Indoor floors, light ramps, equipment enclosures, maintenance walkways",
    zh: "室内通道、轻型坡道、设备机箱罩板、检修走道",
    de: "Innenböden, leichte Rampen, Geräteverkleidungen, Wartungsstege",
    es: "Suelos interiores, rampas ligeras, carcasas de equipos, pasarelas de mantenimiento",
    fr: "Sols intérieurs, rampes légères, capotages d'équipements, passerelles d'entretien",
    pt: "Pisos internos, rampas leves, gabinetes de equipamentos, passarelas de manutenção"
  },
  thickness: {
    en: "Base Thickness: 1.5 mm – 10.0 mm",
    zh: "基板厚度：1.5 mm – 10.0 mm",
    de: "Grundblechdicke: 1,5 mm – 10,0 mm",
    es: "Espesor base: 1,5 mm – 10,0 mm",
    fr: "Épaisseur de base : 1,5 mm – 10,0 mm",
    pt: "Espessura base: 1,5 mm – 10,0 mm"
  },
  ribHeight: {
    en: "Lug Height: 1.0 mm – 2.2 mm",
    zh: "凸棱高度：1.0 mm – 2.2 mm",
    de: "Riffelhöhe: 1,0 mm – 2,2 mm",
    es: "Altura de resalte: 1,0 mm – 2,2 mm",
    fr: "Hauteur de relief : 1,0 mm – 2,2 mm",
    pt: "Altura do relevo: 1,0 mm – 2,2 mm"
  }
}, {
  id: "two-bar",
  title: {
    en: "Two-Bar Checkered Plate",
    zh: "两筋花纹钢板",
    de: "2-Steg-Riffelblech",
    es: "Chapa lagrimada de dos barras",
    fr: "Tôle larmée à 2 stries",
    pt: "Chapa xadrez duas barras"
  },
  badge: {
    en: "Classic Profile",
    zh: "经典双筋",
    de: "Klassisches Profil",
    es: "Perfil clásico",
    fr: "Profil classique",
    pt: "Perfil clássico"
  },
  type: {
    en: "Rolled Raised Pattern",
    zh: "热轧凸纹型",
    de: "Warmgewalztes Riffelprofil",
    es: "Perfil laminado en relieve",
    fr: "Profil laminé en relief",
    pt: "Perfil laminado em relevo"
  },
  to: "/TwoBarCheckeredPlatePage",
  desc: {
    en: "Parallel two-rib raised pattern providing dependable directional traction and straightforward fabrication.",
    zh: "双肋平行凸起构造，提供稳定定向抓地力与良好的冲裁折弯加工适应性。",
    de: "Parallele 2-Steg-Riffelung für verlässliche Richtungsreibung und problemlose Kaltverformung.",
    es: "Patrón de dos nervaduras paralelas que proporciona tracción direccional confiable y facilidad de conformado.",
    fr: "Motif à deux stries parallèles assurant une adhérence directionnelle fiable et un formage aisé.",
    pt: "Padrão de duas nervuras paralelas oferecendo tração direcional confiável e facilidade de conformação."
  },
  bestFor: {
    en: "Plant walkways, utility trailers, equipment skirts, general storage floors",
    zh: "车间通道、挂车地坪、设备裙边防护、仓储地面",
    de: "Werkswege, Nutzanhänger, Maschinenverkleidungen, Lagerböden",
    es: "Pasarelas de taller, remolques utilitarios, faldones de maquinaria, suelos de almacén",
    fr: "Passerelles d'atelier, remorques utilitaires, jupes d'équipements, sols de stockage",
    pt: "Passarelas industriais, reboques utilitários, proteções de máquinas, pisos de armazém"
  },
  thickness: {
    en: "Base Thickness: 2.0 mm – 8.0 mm",
    zh: "基板厚度：2.0 mm – 8.0 mm",
    de: "Grundblechdicke: 2,0 mm – 8,0 mm",
    es: "Espesor base: 2,0 mm – 8,0 mm",
    fr: "Épaisseur de base : 2,0 mm – 8,0 mm",
    pt: "Espessura base: 2,0 mm – 8,0 mm"
  },
  ribHeight: {
    en: "Rib Height: 1.0 mm – 1.8 mm",
    zh: "花纹高度：1.0 mm – 1.8 mm",
    de: "Riffelhöhe: 1,0 mm – 1,8 mm",
    es: "Altura de nervadura: 1,0 mm – 1,8 mm",
    fr: "Hauteur de strie : 1,0 mm – 1,8 mm",
    pt: "Altura da nervura: 1,0 mm – 1,8 mm"
  }
}, {
  id: "five-bar",
  title: {
    en: "Five-Bar Checkered Plate",
    zh: "五筋花纹钢板",
    de: "5-Steg-Riffelblech (Duett/Quintett)",
    es: "Chapa lagrimada de cinco barras",
    fr: "Tôle larmée à 5 stries",
    pt: "Chapa xadrez cinco barras"
  },
  badge: {
    en: "Heavy-Duty Standard",
    zh: "通用高荷",
    de: "Schwerlast-Standard",
    es: "Estándar de alta resistencia",
    fr: "Standard haute résistance",
    pt: "Padrão de serviço pesado"
  },
  type: {
    en: "Rolled Raised Pattern",
    zh: "热轧凸纹型",
    de: "Warmgewalztes Riffelprofil",
    es: "Perfil laminado en relieve",
    fr: "Profil laminé en relief",
    pt: "Perfil laminado em relevo"
  },
  to: "/FiveBarCheckeredPlatePage",
  desc: {
    en: "Five parallel ribs arranged in alternating orthogonal groups, delivering robust heavy-duty traction.",
    zh: "五条平行交错纹理呈正交阵列排布，工业平台及安全楼梯踏板常见标配。",
    de: "Fünf parallel verlaufende Stege in abwechselnd orthogonalen Gruppen für maximale Trittsicherheit.",
    es: "Cinco nervaduras paralelas dispuestas en grupos ortogonales alternados para una adherencia robusta y pesada.",
    fr: "Cinq stries parallèles disposées en groupes orthogonaux alternés, assurant une adhérence robuste pour usage intensif.",
    pt: "Cinco nervuras paralelas dispostas em grupos ortogonais alternados, garantindo tração robusta para serviço pesado."
  },
  bestFor: {
    en: "Heavy industrial platforms, stair treads, loading docks, truck beds",
    zh: "重型工业平台、楼梯踏步、装卸货台、卡车车厢底板",
    de: "Schwere Industrieplattformen, Treppenstufen, Laderampen, Lkw-Ladeflächen",
    es: "Plataformas industriales pesadas, peldaños de escalera, muelles de carga, plataformas de camión",
    fr: "Plates-formes industrielles lourdes, marches d'escalier, quais de chargement, bennes de camion",
    pt: "Plataformas industriais pesadas, degraus de escada, docas de carga, carrocerias de caminhão"
  },
  thickness: {
    en: "Base Thickness: 2.5 mm – 12.0 mm",
    zh: "基板厚度：2.5 mm – 12.0 mm",
    de: "Grundblechdicke: 2,5 mm – 12,0 mm",
    es: "Espesor base: 2,5 mm – 12,0 mm",
    fr: "Épaisseur de base : 2,5 mm – 12,0 mm",
    pt: "Espessura base: 2,5 mm – 12,0 mm"
  },
  ribHeight: {
    en: "Rib Height: 1.2 mm – 2.0 mm",
    zh: "花纹高度：1.2 mm – 2.0 mm",
    de: "Riffelhöhe: 1,2 mm – 2,0 mm",
    es: "Altura de nervadura: 1,2 mm – 2,0 mm",
    fr: "Hauteur de strie : 1,2 mm – 2,0 mm",
    pt: "Altura da nervura: 1,2 mm – 2,0 mm"
  }
}, {
  id: "fish-eye",
  title: {
    en: "Fish-Eye Anti-Slip Plate",
    zh: "鱼眼孔防滑板",
    de: "Rundnoppen-Sicherheitsblech",
    es: "Chapa antideslizante ojo de pez",
    fr: "Tôle antidérapante à emboutis ronds",
    pt: "Chapa antiderrapante olho de peixe"
  },
  badge: {
    en: "Perforated & Raised",
    zh: "冲孔凸起",
    de: "Gelocht & Durchgeprägt",
    es: "Perforado y abombado",
    fr: "Perforé et embouti",
    pt: "Perfurado e repuxado"
  },
  type: {
    en: "Punched Profile Sheet",
    zh: "冲孔异型板",
    de: "Gestanztes Profilblech",
    es: "Chapa perfilada troquelada",
    fr: "Tôle profilée perforée",
    pt: "Chapa perfilada perfurada"
  },
  to: "/FishEyeAntiSlipPlatePage",
  desc: {
    en: "Punched circular raised fish-eye openings combining multidirectional grip, liquid drainage, and light self-cleaning.",
    zh: "冲压圆形凸起鱼眼孔形构造，兼备多向抗滑抓地力与液体污渍自排泄导流特性。",
    de: "Gestanzte, aufgewölbte Rundnoppen verbinden multidirektionale Rutschhemmung mit Flüssigkeitsablauf und Selbstreinigung.",
    es: "Perforaciones circulares embutidas en relieve que combinan agarre multidireccional, drenaje de líquidos y autolimpieza.",
    fr: "Emboutis ronds perforés combinant une adhérence multidirectionnelle, l'évacuation des liquides et l'auto-nettoyage.",
    pt: "Aberturas circulares repuxadas que combinam aderência multidirecional, drenagem de líquidos e ação autolimpante."
  },
  bestFor: {
    en: "Wash bays, food plants, beverage processing, elevated catwalks with drainage",
    zh: "清洗作业区、食品车间、饮料加工线、需要快速排水的架空栈桥",
    de: "Waschplätze, Lebensmittelbetriebe, Getränkeindustrie, entwässerte Laufstege",
    es: "Zonas de lavado, plantas alimentarias, embotelladoras, pasarelas elevadas con drenaje",
    fr: "Aires de lavage, agroalimentaire, brasseries, passerelles surélevées avec drainage",
    pt: "Áreas de lavagem, indústrias alimentícias, fábricas de bebidas, passarelas elevadas com drenagem"
  },
  thickness: {
    en: "Base Thickness: 1.5 mm – 4.0 mm",
    zh: "基板厚度：1.5 mm – 4.0 mm",
    de: "Grundblechdicke: 1,5 mm – 4,0 mm",
    es: "Espesor base: 1,5 mm – 4,0 mm",
    fr: "Épaisseur de base : 1,5 mm – 4,0 mm",
    pt: "Espessura base: 1,5 mm – 4,0 mm"
  },
  ribHeight: {
    en: "Hole Diameter: 8.0 mm – 16.0 mm",
    zh: "冲孔孔径：8.0 mm – 16.0 mm",
    de: "Lochdurchmesser: 8,0 mm – 16,0 mm",
    es: "Diámetro de orificio: 8,0 mm – 16,0 mm",
    fr: "Diamètre de trou : 8,0 mm – 16,0 mm",
    pt: "Diâmetro do furo: 8,0 mm – 16,0 mm"
  }
}, {
  id: "crocodile-mouth",
  title: {
    en: "Crocodile Mouth Anti-Slip Plate",
    zh: "鳄鱼嘴防滑板",
    de: "Sicherheitsrost (Grip Strut)",
    es: "Chapa antideslizante boca de cocodrilo",
    fr: "Tôle antidérapante à crans dentelés",
    pt: "Chapa antiderrapante boca de jacaré"
  },
  badge: {
    en: "Slit & Lifted",
    zh: "开缝凸起",
    de: "Geschlitzt & Aufgestellt",
    es: "Ranurado y levantado",
    fr: "Fendu et relevé",
    pt: "Fendido e repuxado"
  },
  type: {
    en: "Punched Profile Sheet",
    zh: "冲孔异型板",
    de: "Gestanztes Profilblech",
    es: "Chapa perfilada troquelada",
    fr: "Tôle profilée perforée",
    pt: "Chapa perfilada perfurada"
  },
  to: "/CrocodileMouthAntiSlipPlatePage",
  desc: {
    en: "Slit-and-raised serrated crocodile-mouth openings delivering maximum traction, heavy debris clearance, and high airflow.",
    zh: "冲剪拉伸锯齿形鳄鱼嘴开孔，在严苛油污与冰雪工况下提供高摩擦咬合与通风透水。",
    de: "Gezahnte, aufgestellte Rautenöffnungen für höchste Griffigkeit, Schlammdurchfall und hohe Luftdurchlässigkeit.",
    es: "Aberturas dentadas troqueladas y elevadas que proporcionan tracción máxima, evacuación de suciedad densa y alta ventilación.",
    fr: "Ouvertures crantées et relevées assurant une traction maximale, l'évacuation des débris lourds et une ventilation optimale.",
    pt: "Aberturas denteadas fendidase repuxadas que oferecem tração máxima, evacuação de detritos pesados e alta ventilação."
  },
  bestFor: {
    en: "Outdoor drilling rigs, mining plants, oily ramps, exterior stairways in freezing weather",
    zh: "室外钻井作业面、采矿选矿厂、重度油污坡道、严寒冰雪露天楼梯",
    de: "Bohrinseln, Bergbauanlagen, verölte Rampen, Außentreppen bei Schnee und Eis",
    es: "Plataformas de perforación, minería, rampas con aceite, escaleras exteriores con hielo y nieve",
    fr: "Plates-formes de forage, exploitations minières, rampes huileuses, escaliers extérieurs par temps de gel",
    pt: "Plataformas de perfuração, mineradoras, rampas oleosas, escadas externas sujeitas a gelo e neve"
  },
  thickness: {
    en: "Base Thickness: 1.8 mm – 5.0 mm",
    zh: "基板厚度：1.8 mm – 5.0 mm",
    de: "Grundblechdicke: 1,8 mm – 5,0 mm",
    es: "Espesor base: 1,8 mm – 5,0 mm",
    fr: "Épaisseur de base : 1,8 mm – 5,0 mm",
    pt: "Espessura base: 1,8 mm – 5,0 mm"
  },
  ribHeight: {
    en: "Opening Length: 40.0 mm – 65.0 mm",
    zh: "开孔跨度：40.0 mm – 65.0 mm",
    de: "Öffnungslänge: 40,0 mm – 65,0 mm",
    es: "Longitud de abertura: 40,0 mm – 65,0 mm",
    fr: "Longueur d'ouverture : 40,0 mm – 65,0 mm",
    pt: "Comprimento da abertura: 40,0 mm – 65,0 mm"
  }
}];
interface UseCaseRow {
  application: LocalizedText;
  suggested: LocalizedText;
  material: LocalizedText;
  altNote: LocalizedText;
}
const useCaseMatrix: UseCaseRow[] = [{
  application: {
    en: "Walkways & Catwalks (Dry / General)",
    zh: "室内及干燥通用人行走道 / 平台",
    de: "Laufstege & Podeste (trocken / allgemein)",
    es: "Pasarelas y plataformas (secas / generales)",
    fr: "Passerelles et coursives (sèches / générales)",
    pt: "Passarelas e plataformas (secas / gerais)"
  },
  suggested: {
    en: "Five-Bar or Diamond Plate",
    zh: "五筋或菱形花纹钢板",
    de: "5-Steg- oder Rauten-Riffelblech",
    es: "Chapa de cinco barras o diamante",
    fr: "Tôle à 5 stries ou losange",
    pt: "Chapa cinco barras ou diamante"
  },
  altNote: {
    en: "Bar Grating if debris must fall through",
    zh: "如需粉尘杂物自然下落，建议选择钢格板",
    de: "Gitterrost falls Schmutz durchfallen muss",
    es: "Rejilla de barras si la suciedad debe caer a través",
    fr: "Caillebotis si les débris doivent passer à travers",
    pt: "Grelha de barras se resíduos precisarem cair através"
  },
  material: {
    en: "Carbon steel (galvanized) or Aluminum",
    zh: "碳钢（镀锌）或铝合金",
    de: "Kohlenstoffstahl (verzinkt) oder Aluminium",
    es: "Acero al carbono (galvanizado) o aluminio",
    fr: "Acier au carbone (galvanisé) ou aluminium",
    pt: "Aço carbono (galvanizado) ou alumínio"
  }
}, {
  application: {
    en: "Wet, Oily, or Marine Walkways",
    zh: "潮湿、油污或冲洗作业通道",
    de: "Nasse, verölte oder maritime Laufwege",
    es: "Pasarelas húmedas, aceitosas o marinas",
    fr: "Passerelles humides, huileuses ou maritimes",
    pt: "Passarelas úmidas, oleosas ou marinhas"
  },
  suggested: {
    en: "Fish-Eye or Crocodile Mouth Plate",
    zh: "鱼眼孔或鳄鱼嘴冲孔防滑板",
    de: "Rundnoppen- oder Grip-Strut-Sicherheitsblech",
    es: "Chapa ojo de pez o boca de cocodrilo",
    fr: "Tôle à emboutis ronds ou à crans dentelés",
    pt: "Chapa olho de peixe ou boca de jacaré"
  },
  altNote: {
    en: "Serrated Bar Grating also viable",
    zh: "亦可选用齿形钢格板",
    de: "Zahngitterrost ebenfalls geeignet",
    es: "Rejilla dentada también viable",
    fr: "Caillebotis cranté également envisageable",
    pt: "Grelha dentada também viável"
  },
  material: {
    en: "Galvanized carbon steel or Stainless 304/316",
    zh: "热浸镀锌碳钢或304/316不锈钢",
    de: "Feuerverzinkter Stahl oder Edelstahl 304/316",
    es: "Acero al carbono galvanizado o acero inoxidable 304/316",
    fr: "Acier au carbone galvanisé ou inox 304/316",
    pt: "Aço carbono galvanizado ou aço inoxidável 304/316"
  }
}, {
  application: {
    en: "Industrial Stair Treads",
    zh: "工业梯段与楼梯踏步板",
    de: "Industrie-Treppenstufen",
    es: "Peldaños de escalera industriales",
    fr: "Marches d'escalier industrielles",
    pt: "Degraus de escada industriais"
  },
  suggested: {
    en: "Five-Bar Plate (with nosing) or Crocodile Mouth",
    zh: "带防滑前沿的五筋板或鳄鱼嘴防滑板",
    de: "5-Steg-Blech (mit Antrittskante) oder Grip Strut",
    es: "Chapa de cinco barras (con cantonera) o boca de cocodrilo",
    fr: "Tôle à 5 stries (avec nez de marche) ou à crans dentelés",
    pt: "Chapa cinco barras (com testeira antiderrapante) ou boca de jacaré"
  },
  altNote: {
    en: "Standard bolt-on stair treads available",
    zh: "标准螺栓固定式楼梯踏步板可按需协调",
    de: "Verschraubbare Treppenstufen auf Anfrage koordinierbar",
    es: "Peldaños atornillables disponibles bajo pedido",
    fr: "Marches à boulonner disponibles sur demande",
    pt: "Degraus aparafusáveis disponíveis sob consulta"
  },
  material: {
    en: "Carbon steel (HDG) or Aluminum",
    zh: "碳钢（热浸镀锌）或铝合金",
    de: "Kohlenstoffstahl (feuerverzinkt) oder Aluminium",
    es: "Acero al carbono (galvanizado en caliente) o aluminio",
    fr: "Acier au carbone (galvanisé à chaud) ou aluminium",
    pt: "Aço carbono (galvanizado a fogo) ou alumínio"
  }
}, {
  application: {
    en: "Truck Beds & Utility Trailer Floors",
    zh: "卡车车厢底板与挂车地坪",
    de: "Lkw-Ladeflächen & Anhängerböden",
    es: "Plataformas de camión y remolques",
    fr: "Bennes de camion et planchers de remorques",
    pt: "Caçambas de caminhão e pisos de carretas"
  },
  suggested: {
    en: "Two-Bar or Five-Bar Plate",
    zh: "两筋或五筋花纹钢板",
    de: "2-Steg- oder 5-Steg-Riffelblech",
    es: "Chapa de dos o cinco barras",
    fr: "Tôle à 2 ou 5 stries",
    pt: "Chapa de duas ou cinco barras"
  },
  altNote: {
    en: "Provides solid containment for cargo",
    zh: "实心板面防止货物渗漏与散落",
    de: "Geschlossene Fläche sichert Schüttgut",
    es: "Superficie continua que retiene la carga",
    fr: "Surface pleine retenant les charges",
    pt: "Superfície sólida para contenção de carga"
  },
  material: {
    en: "Heavy-gauge carbon steel or Aluminum",
    zh: "厚规格碳钢或轻量化铝合金",
    de: "Dickwandiger Kohlenstoffstahl oder Aluminium",
    es: "Acero al carbono de grueso calibre o aluminio",
    fr: "Acier au carbone forte épaisseur ou aluminium",
    pt: "Aço carbono de alta espessura ou alumínio"
  }
}, {
  application: {
    en: "Vehicle Running Boards & Steps",
    zh: "工程车辆登车踏板与迎宾踏板",
    de: "Fahrzeugtrittstufen & Einstiege",
    es: "Estribos y escalones de vehículos",
    fr: "Marchepieds et marchettes de véhicules",
    pt: "Estribos e degraus de veículos"
  },
  suggested: {
    en: "Diamond or Fish-Eye Plate",
    zh: "菱形花纹板或鱼眼孔防滑板",
    de: "Rauten- oder Rundnoppen-Blech",
    es: "Chapa de diamante u ojo de pez",
    fr: "Tôle losange ou à emboutis ronds",
    pt: "Chapa diamante ou olho de peixe"
  },
  altNote: {
    en: "Lightweight and easy to form/bend",
    zh: "自重轻且便于冲压剪折成型",
    de: "Geringes Gewicht, gut kantbar",
    es: "Ligera y fácil de conformar/plegar",
    fr: "Légère et facile à découper/plier",
    pt: "Leve e fácil de dobrar/conformar"
  },
  material: {
    en: "Aluminum 3003 / 5052 / 6061",
    zh: "铝合金（3003 / 5052 / 6061）",
    de: "Aluminium 3003 / 5052 / 6061",
    es: "Aluminio 3003 / 5052 / 6061",
    fr: "Aluminium 3003 / 5052 / 6061",
    pt: "Alumínio 3003 / 5052 / 6061"
  }
}, {
  application: {
    en: "Equipment Panels & Maintenance Access",
    zh: "设备检修盖板与机壳防滑顶盖",
    de: "Wartungsabdeckungen & Maschinendeckel",
    es: "Tapas de mantenimiento y cubiertas de máquinas",
    fr: "Panneaux d'accès maintenance et capots",
    pt: "Tampas de inspeção e coberturas de máquinas"
  },
  suggested: {
    en: "Diamond or Two-Bar Plate",
    zh: "菱形或两筋花纹钢板",
    de: "Rauten- oder 2-Steg-Riffelblech",
    es: "Chapa de diamante o dos barras",
    fr: "Tôle losange ou à 2 stries",
    pt: "Chapa diamante ou duas barras"
  },
  altNote: {
    en: "Clean appearance with structural rigidity",
    zh: "外观规整且兼具板材刚度",
    de: "Saubere Optik mit hoher Eigensteifigkeit",
    es: "Aspecto limpio con rigidez estructural",
    fr: "Aspect soigné avec rigidité mécanique",
    pt: "Visual limpo com rigidez estrutural"
  },
  material: {
    en: "Carbon steel (painted) or Stainless 304",
    zh: "碳素钢（防锈喷漆）或304不锈钢",
    de: "Kohlenstoffstahl (lackiert) oder Edelstahl 304",
    es: "Acero al carbono (pintado) o acero inoxidable 304",
    fr: "Acier au carbone (peint) ou inox 304",
    pt: "Aço carbono (pintado) ou aço inoxidável 304"
  }
}, {
  application: {
    en: "Architectural & Decorative Flooring",
    zh: "商业建筑与展示空间装饰地面",
    de: "Architektonische & dekorative Bodenbeläge",
    es: "Suelos arquitectónicos y decorativos",
    fr: "Revêtements de sol décoratifs et architecturaux",
    pt: "Pisos arquitetônicos e decorativos"
  },
  suggested: {
    en: "Diamond or Two-Bar Plate",
    zh: "菱形或两筋花纹钢板",
    de: "Rauten- oder 2-Steg-Riffelblech",
    es: "Chapa de diamante o dos barras",
    fr: "Tôle losange ou à 2 stries",
    pt: "Chapa diamante ou duas barras"
  },
  altNote: {
    en: "Regular geometric aesthetic",
    zh: "规整几何阵列质感",
    de: "Gleichmäßige geometrische Anmutung",
    es: "Estética geométrica regular",
    fr: "Esthétique géométrique régulière",
    pt: "Estética geométrica uniforme"
  },
  material: {
    en: "Stainless steel (satin/mill) or Anodized Aluminum",
    zh: "不锈钢（拉丝/原面）或阳极氧化铝板",
    de: "Edelstahl (satiniert/walzblank) oder eloxiertes Aluminium",
    es: "Acero inoxidable (satinado/bruto) o aluminio anodizado",
    fr: "Inox (satiné/brut) ou aluminium anodisé",
    pt: "Aço inoxidável (escovado/bruto) ou alumínio anodizado"
  }
}];
interface CommonMistake {
  mistake: LocalizedText;
  desc: LocalizedText;
}
const commonMistakes: CommonMistake[] = [{
  mistake: {
    en: "Choosing by pattern visual appeal only",
    zh: "仅凭外观视觉选型，忽视真实工况荷载",
    de: "Auswahl rein nach optischer Riffelung",
    es: "Elección basada únicamente en el aspecto visual",
    fr: "Choisir uniquement selon l'aspect visuel",
    pt: "Escolher apenas pela estética do relevo"
  },
  desc: {
    en: "A diamond pattern that looks clean may pack with grease or mud in outdoor industrial plants. Severe environments require self-draining punched profiles like crocodile mouth or fish-eye.",
    zh: "在室外泥水或油污工况下，仅考虑表面花纹美观会导致防滑失效。重度污染环境需选用具备排屑排液功能的鳄鱼嘴或鱼眼孔冲孔板。",
    de: "Ein optisch sauberes Rautenprofil kann sich in rauen Industrieumgebungen mit Schlamm und Fett zusetzen. Extreme Bedingungen verlangen selbstreinigende, gelochte Profile wie Grip Strut oder Rundnoppen.",
    es: "Un patrón de diamante puede saturarse de grasa o barro en entornos industriales exteriores. Condiciones severas requieren perfiles troquelados autodrenantes como boca de cocodrilo u ojo de pez.",
    fr: "Un motif losange peut se colmater de graisse ou de boue en extérieur. Les environnements sévères requièrent des profils perforés autodrainants tels que les crans dentelés ou les emboutis ronds.",
    pt: "Um padrão diamante visualmente limpo pode acumular graxa ou lama ao ar livre. Ambientes severos exigem perfis perfurados autodrenantes como boca de jacaré ou olho de peixe."
  }
}, {
  mistake: {
    en: "Ignoring the difference between base thickness and total height",
    zh: "混淆基板净厚度与含花纹总厚度",
    de: "Verwechslung von Grundblechdicke und Gesamthöhe",
    es: "Ignorar la diferencia entre espesor base y altura total",
    fr: "Confondre l'épaisseur de base et la hauteur totale",
    pt: "Ignorar a diferença entre espessura base e altura total"
  },
  desc: {
    en: "Load tables and structural calculations rely strictly on base plate thickness (t), excluding the raised lug or rib height. Specifying total height instead can cause under-designed load capacity.",
    zh: "力学验算与荷载承载力完全取决于基板净厚度（t），不包含凸起花纹高度。若将含花纹总厚度作为受力厚度计算，将导致严重承载不足。",
    de: "Tragfähigkeitsberechnungen beruhen ausschließlich auf der Grundblechdicke (t) ohne Riffelhöhe. Wird fälschlicherweise die Gesamthöhe angesetzt, entsteht eine gefährliche statische Unterdimensionierung.",
    es: "Los cálculos de carga dependen estrictamente del espesor de la chapa base (t), excluyendo la altura del relieve. Tomar la altura total provoca un subdimensionamiento estructural peligroso.",
    fr: "Les calculs de charge reposent strictement sur l'épaisseur de tôle de base (t), sans le relief. Spécifier la hauteur totale entraîne un sous-dimensionnement critique de la capacité portante.",
    pt: "Os cálculos estruturais dependem exclusivamente da espessura da chapa base (t), desconsiderando a altura do relevo. Usar a altura total gera subdimensionamento de carga."
  }
}, {
  mistake: {
    en: "Ignoring drainage and oil accumulation",
    zh: "忽视积水、积雪与油脂积聚隐患",
    de: "Vernachlässigung von Wasser- und Ölablauf",
    es: "Ignorar el drenaje y la acumulación de aceite",
    fr: "Négliger le drainage et l'accumulation d'huile",
    pt: "Ignorar a drenagem e o acúmulo de óleo"
  },
  desc: {
    en: "Solid rolled plate holds liquids on the surface. Where water, grease, or freezing precipitation creates slipping hazards, punched open profiles or open bar grating must be evaluated.",
    zh: "实心滚轧花纹板不具备板面自排液能力。如果现场存在积水、油污或雨雪结冰风险，应优先评估冲孔型防滑板或透空钢格板。",
    de: "Geschlossene Riffelbleche stauen Flüssigkeiten. Bei Gefahr durch Wasser, Fett oder Vereisung sind offene Sicherheitsbleche oder Gitterroste die technisch gebotene Lösung.",
    es: "La chapa laminada sólida retiene líquidos en la superficie. Donde el agua, la grasa o la nieve generen riesgo de caída, deben evaluarse perfiles perforados abiertos o rejilla de barras.",
    fr: "Une tôle pleine retient les liquides en surface. Lorsque l'eau, la graisse ou le gel créent des risques de glissade, privilégiez les tôles profilées ouvertes ou les caillebotis.",
    pt: "A chapa sólida retém líquidos na superfície. Em locais com água, óleo ou gelo gerando risco de escorregamento, avalie perfis perfurados abertos ou grelhas de barras."
  }
}, {
  mistake: {
    en: "Underestimating span deflection without intermediate supports",
    zh: "跨距支撑间距过大，低估板面挠度与形变",
    de: "Unterschätzung der Durchbiegung bei zu großen Stützweiten",
    es: "Subestimar la flexión por falta de apoyos intermedios",
    fr: "Sous-estimer la flèche sans supports intermédiaires",
    pt: "Subestimar a deflexão sem suportes intermediários"
  },
  desc: {
    en: "Unlike bar grating with deep vertical bearing bars, solid checkered plate has limited stiffness across longer spans. Adequate joist spacing or stiffener ribs must be verified.",
    zh: "与带有深截面立向承载扁钢的钢格板不同，实心平铺花纹钢板在大跨距下的抗弯刚度有限。大跨度下必须设置合理的龙骨梁间距或加劲肋。",
    de: "Anders als Gitterroste mit hohen Tragstäben besitzt Riffelblech bei großen Stützweiten nur begrenzte Biegesteifigkeit. Ausreichend enge Trägerabstände oder Versteifungsrippen sind Pflicht.",
    es: "A diferencia de la rejilla con pletinas portantes verticales, la chapa lagrimada maciza tiene rigidez limitada a grandes vanos. Debe verificarse la distancia entre viguetas o refuerzos.",
    fr: "Contrairement au caillebotis doté de barres porteuses verticales, la tôle larmée possède une rigidité limitée sur les grandes portées. L'espacement des traverses ou des raidisseurs doit être vérifié.",
    pt: "Diferente da grelha de barras com barras portantes verticais, a chapa xadrez sólida tem rigidez limitada em vãos maiores. O espaçamento entre vigas ou nervuras de reforço deve ser verificado."
  }
}, {
  mistake: {
    en: "Mismatched coating for outdoor or aggressive chemical exposure",
    zh: "腐蚀工况下涂层或材质选型不匹配",
    de: "Ungeeigneter Korrosionsschutz im Außen- oder Chemiebereich",
    es: "Recubrimiento incompatible en exteriores o ambientes agresivos",
    fr: "Revêtement inadapté en extérieur ou milieu corrosif",
    pt: "Revestimento inadequado para ambientes externos ou corrosivos"
  },
  desc: {
    en: "Mill finish or simple shop primer on carbon steel rusts rapidly outdoors. Harsh weather requires hot-dip galvanizing (confirmed per project) or stainless steel (304/316).",
    zh: "室外潮湿工况下若采用碳钢原面或简单底漆，易产生氧化锈蚀。恶劣室外工况应选用热浸镀锌处理（视项目确认），或直接采用304/316不锈钢。",
    de: "Walzblanker Stahl oder Grundierung korrodiert im Freien rasch. Raue Witterung erfordert Feuerverzinkung (projektbezogen bestätigt) oder den Einsatz von Edelstahl (304/316).",
    es: "El acero bruto o con imprimación simple se oxida rápidamente en exteriores. Climas exigentes requieren galvanizado en caliente (confirmado según el proyecto) o acero inoxidable (304/316).",
    fr: "L'acier brut ou avec primaire simple rouille vite en extérieur. Les intempéries requièrent une galvanisation à chaud (confirmée selon le projet) ou de l'acier inoxydable (304/316).",
    pt: "Aço carbono bruto ou com primer simples oxida rapidamente ao ar livre. Condições climáticas severas exigem galvanização a fogo (confirmada conforme o projeto) ou aço inoxidável (304/316)."
  }
}];
const decisionFlowSteps: DecisionStep[] = [{
  question: {
    en: "Step 1: What is the operating environment?",
    zh: "第一步：使用现场属于哪种环境工况？",
    de: "Schritt 1: Welche Umgebungsbedingungen herrschen vor?",
    es: "Paso 1: ¿Cuál es el entorno de servicio?",
    fr: "Étape 1 : Quel est l'environnement d'exploitation ?",
    pt: "Passo 1: Qual é o ambiente de operação?"
  },
  options: [{
    label: {
      en: "Indoor / dry storage / machinery covers",
      zh: "室内干燥仓储 / 设备防护盖板",
      de: "Trockener Innenbereich / Abdeckungen",
      es: "Interior seco / tapas de equipos",
      fr: "Intérieur sec / capotages de machines",
      pt: "Ambiente interno seco / tampas de máquinas"
    },
    result: {
      en: "Diamond or Two-Bar (Mill / Painted)",
      zh: "菱形或两筋板（原面 / 防锈漆）",
      de: "Rauten oder 2-Steg (walzblank / lackiert)",
      es: "Diamante o 2 barras (bruto / pintado)",
      fr: "Losange ou 2 stries (brut / peint)",
      pt: "Diamante ou 2 barras (bruto / pintado)"
    }
  }, {
    label: {
      en: "Outdoor wet / exterior walkways & ramps",
      zh: "室外雨水暴露 / 露天人行通道与坡道",
      de: "Außenbereich feucht / Stege & Rampen",
      es: "Exterior húmedo / pasarelas y rampas",
      fr: "Extérieur humide / passerelles & rampes",
      pt: "Externo úmido / passarelas e rampas"
    },
    result: {
      en: "Five-Bar HDG or Punched Profile",
      zh: "五筋板热浸镀锌或冲孔防滑板",
      de: "5-Steg feuerverzinkt oder Profilblech",
      es: "5 barras galvanizado o chapa perforada",
      fr: "5 stries galvanisé ou tôle perforée",
      pt: "5 barras galvanizado a fogo ou chapa perfurada"
    }
  }, {
    label: {
      en: "Oily / wash bays / marine / chemical",
      zh: "重度油污 / 冲洗车间 / 海洋盐雾 / 化工",
      de: "Ölig / Waschplätze / Chemie / Maritim",
      es: "Aceitoso / zonas de lavado / químico / marino",
      fr: "Huileux / aires de lavage / marin / chimique",
      pt: "Oleoso / áreas de lavagem / marítimo / químico"
    },
    result: {
      en: "Crocodile Mouth / Fish-Eye (Stainless or HDG)",
      zh: "鳄鱼嘴 / 鱼眼孔冲孔板（不锈钢或热镀锌）",
      de: "Grip Strut / Rundnoppen (Edelstahl/HDG)",
      es: "Boca de cocodrilo / Ojo de pez (Inox/HDG)",
      fr: "Crans dentelés / Emboutis ronds (Inox/HDG)",
      pt: "Boca de jacaré / Olho de peixe (Inox/HDG)"
    }
  }]
}, {
  question: {
    en: "Step 2: What traffic and loading type is expected?",
    zh: "第二步：预期承受何种通行方式与载荷？",
    de: "Schritt 2: Welche Verkehrs- und Lastart liegt vor?",
    es: "Paso 2: ¿Qué tipo de tráfico y carga se espera?",
    fr: "Étape 2 : Quel est le type de trafic et de charge ?",
    pt: "Passo 2: Qual é o tipo de tráfego e carregamento previsto?"
  },
  options: [{
    label: {
      en: "Pedestrian foot traffic / general catwalks",
      zh: "常规人员步行通行 / 工业检修步道",
      de: "Reiner Fußgängerverkehr / Wartungsstege",
      es: "Tráfico peatonal / pasarelas generales",
      fr: "Circulation piétonne / coursives d'entretien",
      pt: "Tráfego de pedestres / passarelas de manutenção"
    },
    result: {
      en: "Base gauge 2.5–4.5 mm (Confirmed per project)",
      zh: "基板厚度 2.5–4.5 mm（按项目确认）",
      de: "Grundblech 2,5–4,5 mm (je Projekt bestätigt)",
      es: "Espesor base 2,5–4,5 mm (confirmado por proyecto)",
      fr: "Épaisseur base 2,5–4,5 mm (confirmé par projet)",
      pt: "Espessura base 2,5–4,5 mm (confirmado por projeto)"
    }
  }, {
    label: {
      en: "Wheeled carts / mobile utility trailers",
      zh: "推车轮载通行 / 轻型挂车货台",
      de: "Handwagen / leichte Transportanhänger",
      es: "Carros con ruedas / remolques ligeros",
      fr: "Chariots à roulettes / remorques utilitaires",
      pt: "Carrinhos de mão / reboques utilitários"
    },
    result: {
      en: "Solid plate (Two-Bar or Five-Bar 3.0–6.0 mm)",
      zh: "实心滚轧板（两筋或五筋 3.0–6.0 mm）",
      de: "Geschlossenes Blech (2-Steg/5-Steg 3,0–6,0 mm)",
      es: "Chapa maciza (2 o 5 barras 3,0–6,0 mm)",
      fr: "Tôle pleine (2 ou 5 stries 3,0–6,0 mm)",
      pt: "Chapa maciça (2 ou 5 barras 3,0–6,0 mm)"
    }
  }, {
    label: {
      en: "Heavy dynamic loads / forklift / truck beds",
      zh: "叉车重载碾压 / 卡车底板 / 重型货台",
      de: "Schwerlast / Staplerverkehr / Lkw-Pritschen",
      es: "Cargas dinámicas pesadas / carretillas elevadoras",
      fr: "Charges dynamiques lourdes / chariots élévateurs",
      pt: "Cargas dinâmicas pesadas / empilhadeiras / caçambas"
    },
    result: {
      en: "Heavy-gauge 5.0–12.0 mm with dense joists",
      zh: "厚规格 5.0–12.0 mm 并加强梁间支撑",
      de: "Dickblech 5,0–12,0 mm mit enger Trägerteilung",
      es: "Calibre pesado 5,0–12,0 mm con apoyos densos",
      fr: "Forte épaisseur 5,0–12,0 mm et appuis rapprochés",
      pt: "Espessura pesada 5,0–12,0 mm com vigas próximas"
    }
  }]
}, {
  question: {
    en: "Step 3: Which pattern and surface finish fits best?",
    zh: "第三步：哪种花纹形制与表面防护最为适配？",
    de: "Schritt 3: Welches Muster und welche Oberfläche eignen sich?",
    es: "Paso 3: ¿Qué dibujo y acabado superficial se adaptan mejor?",
    fr: "Étape 3 : Quel motif et quelle finition conviennent le mieux ?",
    pt: "Passo 3: Qual padrão de relevo e acabamento são mais adequados?"
  },
  options: [{
    label: {
      en: "Diamond pattern (Classic multidirectional)",
      zh: "菱形花纹（经典多向防滑，整洁质感）",
      de: "Rautenmuster (multidirektional, saubere Optik)",
      es: "Patrón de diamante (multidireccional clásico)",
      fr: "Motif losange (multidirectionnel classique)",
      pt: "Padrão diamante (multidirecional clássico)"
    },
    result: {
      en: "Indoor platforms, steps & equipment panels",
      zh: "室内平台、登车踏步与设备防护盖板",
      de: "Innenbühnen, Stufen & Maschinendeckel",
      es: "Plataformas interiores, peldaños y tapas",
      fr: "Plates-formes intérieures, marches et capots",
      pt: "Plataformas internas, degraus e coberturas"
    }
  }, {
    label: {
      en: "Two-Bar pattern (Directional, easy to form)",
      zh: "两筋花纹（定向防滑，易折弯成型）",
      de: "2-Steg-Muster (richtungsbezogen, gut verformbar)",
      es: "Patrón de 2 barras (tracción direccional, fácil doblado)",
      fr: "Motif 2 stries (directionnel, formage aisé)",
      pt: "Padrão 2 barras (tração direcional, fácil de dobrar)"
    },
    result: {
      en: "Utility trailers, walkways & warehouse floors",
      zh: "挂车货台、车间通道与仓储地坪",
      de: "Nutzanhänger, Werkswege & Lagerböden",
      es: "Remolques utilitarios, pasarelas y almacenes",
      fr: "Remorques, coursives d'atelier & sols d'entrepôt",
      pt: "Reboques, passarelas industriais e pisos de armazém"
    }
  }, {
    label: {
      en: "Five-Bar pattern (Robust industrial grip)",
      zh: "五筋花纹（高荷工业平台与楼梯标准配置）",
      de: "5-Steg-Muster (robuste Trittsicherheit im Industriebau)",
      es: "Patrón de 5 barras (adherencia robusta de alta carga)",
      fr: "Motif 5 stries (adhérence robuste usage intensif)",
      pt: "Padrão 5 barras (tração robusta para serviço pesado)"
    },
    result: {
      en: "Plant catwalks, stair treads & loading bays",
      zh: "厂房平台走道、楼梯踏步与装卸货台",
      de: "Werkslaufstege, Treppenstufen & Laderampen",
      es: "Pasarelas de nave, peldaños y muelles",
      fr: "Passerelles d'usine, marches et quais",
      pt: "Passarelas de fábrica, degraus e docas"
    }
  }]
}];
export default function GuideSteps() {
  const {
    lang
  } = useLanguage();
  return <section id="checkeredplateselectionguide" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Intro */}
        <div className="max-w-3xl mb-10 md:mb-12">
          <Badge variant="outline" className="mb-3 font-mono text-xs uppercase tracking-wider">
            {pick({
            en: "6-Step Selection Framework",
            zh: "六步选型流程",
            de: "6-Schritte-Auswahlverfahren",
            es: "Marco de selección en 6 pasos",
            fr: "Méthode de sélection en 6 étapes",
            pt: "Método de seleção em 6 passos"
          }, lang)}
          </Badge>
          <div className="border-l-[3px] border-accent pl-3 mb-4">
            <h2 className="text-3xl sm:text-4xl font-bold font-serif tracking-tight text-foreground">
              {pick({
              en: "6 Steps to Specify the Right Checkered Plate",
              zh: "花纹钢板工程选型六步法则",
              de: "In 6 Schritten zum passenden Riffelblech",
              es: "6 pasos para especificar la chapa lagrimada correcta",
              fr: "6 étapes pour spécifier la bonne tôle larmée",
              pt: "6 passos para especificar a chapa xadrez correta"
            }, lang)}
            </h2>
          </div>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            {pick({
            en: "Follow these six engineering checkpoints to align profile geometry, structural span, base alloy, and environmental protection with your project specifications.",
            zh: "从应用场景定义到表面防护与技术文件核验，遵循六个严谨步骤，确保选定的板型在承载强度、防滑安全和长期耐腐蚀性上达到工程设计要求。",
            de: "Folgen Sie diesen sechs Prüfpunkten, um Riffelgeometrie, Spannweite, Grundwerkstoff und Oberflächenschutz exakt auf Ihre technischen Vorgaben abzustimmen.",
            es: "Siga estos seis puntos de comprobación para alinear el perfil, vano estructural, aleación y protección ambiental con las especificaciones de su proyecto.",
            fr: "Suivez ces six étapes techniques pour harmoniser profil, portée d'appui, alliage et protection environnementale avec les spécifications de votre projet.",
            pt: "Siga estes seis pontos de verificação para alinhar geometria do relevo, vão estrutural, liga metálica e proteção contra corrosão com o seu projeto."
          }, lang)}
          </p>
        </div>

        {/* DECISION FLOW CHART */}
        <div className="mb-12 md:mb-16">
          <DecisionFlow title={{
          en: "Checkered Plate Selection Decision Flow",
          zh: "花纹钢板工程选型决策路径",
          de: "Entscheidungsbaum zur Riffelblechauswahl",
          es: "Flujo de decisión para selección de chapa lagrimada",
          fr: "Arbre de décision pour le choix d'une tôle larmée",
          pt: "Fluxo de decisão para seleção de chapa xadrez"
        }} steps={decisionFlowSteps} />
        </div>

        {/* THREE-UP PATTERN DIAGRAM FRAME ROW */}
        <div className="mb-12 md:mb-16">
          <div className="mb-6 border-l-[3px] border-accent pl-3">
            <Badge variant="outline" className="mb-2 font-mono text-xs uppercase tracking-wider">
              {pick({
              en: "Pattern Geometry & Cross-Section",
              zh: "花纹排布与截面几何参数",
              de: "Riffelgeometrie & Profilquerschnitt",
              es: "Geometría del relieve y sección transversal",
              fr: "Géométrie du motif et profil en coupe",
              pt: "Geometria do relevo e seção transversal"
            }, lang)}
            </Badge>
            <h3 className="text-2xl sm:text-3xl font-bold font-serif text-foreground">
              {pick({
              en: "Standard Rolled Checkered Plate Profiles",
              zh: "热轧花纹板三大主流几何剖面与参数定义",
              de: "Standard-Riffelblechprofile im Überblick",
              es: "Perfiles estándar de chapa lagrimada laminada",
              fr: "Profils standards de tôles larmées laminées",
              pt: "Perfis padrão de chapa xadrez laminada"
            }, lang)}
            </h3>
            <p className="text-sm sm:text-base text-muted-foreground mt-1 leading-relaxed max-w-3xl">
              {pick({
              en: "Plan view arrangements and cross-sectional parameters for diamond, two-bar, and five-bar raised patterns. Structural calculations depend strictly on base plate thickness (T), excluding raised lug height (H). Dimensions are confirmed per project.",
              zh: "对比菱形、两筋与五筋凸纹的平面排布节距与立体剖面结构。结构承载力严格依据基板净厚度（T）核算，不计入凸起花纹高度（H）。具体尺寸根据项目确认。",
              de: "Draufsicht und Schnittgeometrie für Rauten-, 2-Steg- und 5-Steg-Riffelungen. Statische Berechnungen basieren ausschließlich auf der Grundblechdicke (T) ohne Riffelhöhe (H). Maße werden projektspezifisch bestätigt.",
              es: "Disposición en planta y parámetros de sección transversal para patrones de diamante, dos barras y cinco barras. Los cálculos estructurales dependen estrictamente del espesor base (T), sin la altura del resalte (H). Dimensiones confirmadas por proyecto.",
              fr: "Dispositions en plan et paramètres de coupe pour les motifs losange, à 2 stries et à 5 stries. Les calculs structuraux reposent strictement sur l'épaisseur de base (T), hors relief (H). Dimensions confirmées selon le projet.",
              pt: "Disposição em vista superior e parâmetros de corte para padrões diamante, duas barras e cinco barras. Cálculos estruturais dependem estritamente da espessura base (T), excluindo a altura do relevo (H). Dimensões confirmadas conforme o projeto."
            }, lang)}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <DiagramFrame title={{
            en: "Diamond Pattern (Single Lug)",
            zh: "菱形花纹（单颗扁豆型）",
            de: "Rautenmuster (Einzelraute)",
            es: "Patrón de diamante (lágrima simple)",
            fr: "Motif losange (larme unique)",
            pt: "Padrão diamante (relevo simples)"
          }} caption={{
            en: "Staggered diamond array providing balanced multidirectional foot traction. Clean visual appearance well suited for walkways, vehicle steps, and architectural covers.",
            zh: "交错点状菱形排布，提供均衡的全方位抓地摩擦力，整洁美观，多用于室内走道、登车踏板与设备检修面板。",
            de: "Versetzte Rautenanordnung für ausgewogene Trittsicherheit in allen Richtungen. Saubere Optik für Laufwege, Einstiege und Gehäuseabdeckungen.",
            es: "Disposición en rombos alternados para tracción multidireccional equilibrada. Estética limpia para pasarelas, estribos y tapas técnicas.",
            fr: "Disposition en losanges décalés assurant une adhérence multidirectionnelle équilibrée. Aspect épuré pour passerelles, marchepieds et capotages.",
            pt: "Disposição em losangos alternados oferecendo tração multidirecional equilibrada. Visual limpo para passarelas, estribos e tampas técnicas."
          }}>
              <CheckeredPlateDiagram variant="diamond" />
            </DiagramFrame>

            <DiagramFrame title={{
            en: "Two-Bar Pattern (Duo Ribs)",
            zh: "两筋花纹（双肋平行排布）",
            de: "2-Steg-Muster (Duett-Riffel)",
            es: "Patrón de dos barras (dúo paralelo)",
            fr: "Motif à 2 stries (duo parallèle)",
            pt: "Padrão duas barras (duo paralelo)"
          }} caption={{
            en: "Pairs of parallel raised ribs alternating perpendicularly. Dependable directional traction with excellent cold brake forming and bending behavior.",
            zh: "成对平行排列的双筋构造，交错正交分布。提供定向防滑抓地力与良好的下料冲压及机械折弯适应性。",
            de: "Paarweise angeordnete Rippen in wechselnder Ausrichtung. Zuverlässige Richtungsreibung und exzellentes Kaltverformungsverhalten.",
            es: "Pares de nervaduras paralelas orientadas perpendicularmente. Tracción direccional fiable y excelente comportamiento al plegado en frío.",
            fr: "Paires de stries parallèles disposées perpendiculairement. Adhérence directionnelle fiable et grande facilité de pliage et façonnage.",
            pt: "Pares de nervuras paralelas dispostas perpendicularmente. Tração direcional confiável e excelente conformação em dobras a frio."
          }}>
              <CheckeredPlateDiagram variant="two-bar" />
            </DiagramFrame>

            <DiagramFrame title={{
            en: "Five-Bar Pattern (Quintet Ribs)",
            zh: "五筋花纹（五肋正交柳叶形）",
            de: "5-Steg-Muster (Quintett-Riffel)",
            es: "Patrón de cinco barras (quinteto)",
            fr: "Motif à 5 stries (quintet)",
            pt: "Padrão cinco barras (quinteto)"
          }} caption={{
            en: "Clusters of five parallel ribs alternating orthogonally. Heavy-duty standard for industrial catwalks, high-traffic stair treads, and loading decks.",
            zh: "五条平行凸棱呈直角正交阵列排布。重载工况通用标准，适用于化工厂平台走道、高负荷安全楼梯踏步及装卸货台。",
            de: "Fünfergruppen parallel verlaufender Stege im orthogonalen Wechsel. Schwerlaststandard für Industrielaufstege, Treppen und Laderampen.",
            es: "Grupos de cinco nervaduras paralelas en orientación ortogonal alternada. Estándar para pasarelas industriales, peldaños y muelles de carga.",
            fr: "Groupes de cinq stries parallèles en alternance orthogonale. Standard haute résistance pour passerelles d'atelier, marches et quais de chargement.",
            pt: "Grupos de cinco nervuras paralelas alternadas ortogonalmente. Padrão de serviço pesado para passarelas industriais, degraus e docas de carga."
          }}>
              <CheckeredPlateDiagram variant="five-bar" />
            </DiagramFrame>
          </div>
        </div>

        {/* STEP-BY-STEP VERTICAL TIMELINE */}
        <div className="space-y-12">
          {/* STEP 1: DEFINE USE CASE */}
          <div className="border border-border rounded-lg p-6 sm:p-8 bg-card shadow-xs">
            <div className="flex items-start gap-4 mb-5">
              <div className="size-9 rounded-sm bg-primary text-primary-foreground flex items-center justify-center font-mono font-bold text-base shrink-0">
                1
              </div>
              <div>
                <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground block mb-0.5">
                  {pick({
                  en: "Step 1 · Define the Application",
                  zh: "步骤 1 · 使用场景与工况界定",
                  de: "Schritt 1 · Einsatzbereich & Betriebsbedingungen",
                  es: "Paso 1 · Definir la aplicación y condiciones",
                  fr: "Étape 1 · Définir l'application et l'environnement",
                  pt: "Passo 1 · Definir a aplicação e condições de serviço"
                }, lang)}
                </span>
                <h3 className="text-xl sm:text-2xl font-bold text-foreground">
                  {pick({
                  en: "Determine Load Nature, Traffic Type & Environment",
                  zh: "确定荷载性质、交通类型与环境暴露",
                  de: "Lastart, Verkehrsbeanspruchung & Umgebung ermitteln",
                  es: "Determinar naturaleza de carga, tráfico y entorno",
                  fr: "Déterminer la nature des charges, le trafic et l'exposition",
                  pt: "Determinar a natureza da carga, tráfego e ambiente"
                }, lang)}
                </h3>
              </div>
            </div>

            <p className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-6">
              {pick({
              en: "Identify the primary service function: walkways and platforms, stair treads, vehicle or truck beds and running boards, equipment enclosure panels, or architectural flooring. Each scenario dictates whether you need a solid surface to retain small items or an open punched profile for rapid fluid drainage.",
              zh: "选用板材前，首要明确使用部位的具体功能与受力方式：包括工业检修走道与作业平台、楼梯踏步板、车辆车厢与登车脚踏板、设备防护外罩板，还是商业空间的装饰地面。不同部位对板面平整度、排水透气性及轮载冲击的要求截然不同。",
              de: "Klären Sie die primäre Funktion: Wartungsstege, Treppenstufen, Lkw-Ladeflächen, Trittbretter, Gehäuseabdeckungen oder repräsentative Böden. Dies entscheidet, ob eine geschlossene Fläche zur Schüttgutführung oder ein offenes, drainierendes Profil erforderlich ist.",
              es: "Identifique la función principal: pasarelas y plataformas, peldaños, suelos de vehículos o estribos, paneles de maquinaria o suelos arquitectónicos. Cada uso determina si necesita una superficie continua o un perfil perforado autodrenante.",
              fr: "Identifiez l'usage principal : passerelles, marches d'escalier, planchers de véhicules ou marchepieds, capotages ou sols architecturaux. Cela détermine le besoin d'une surface pleine ou d'un profil ouvert permettant un drainage rapide des fluides.",
              pt: "Identifique a função principal: passarelas e plataformas, degraus de escada, assoalhos de veículos e estribos, painéis de equipamentos ou pisos arquitetônicos. Cada caso dita a necessidade de chapa fechada ou perfil perfurado vazado para drenagem."
            }, lang)}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div className="p-4 rounded border border-border bg-muted/30">
                <span className="font-semibold text-foreground block mb-1">
                  {pick({
                  en: "Walkways & Platforms",
                  zh: "人行步道与操作平台",
                  de: "Laufstege & Arbeitsbühnen",
                  es: "Pasarelas y plataformas",
                  fr: "Passerelles et plates-formes",
                  pt: "Passarelas e plataformas"
                }, lang)}
                </span>
                <p className="text-muted-foreground leading-relaxed">
                  {pick({
                  en: "Prioritize multidirectional foot traction, comfortable walking surface, and deflection between joists.",
                  zh: "重点关注人员通行的多向防滑摩擦力与支撑梁间距带来的板面挠度。",
                  de: "Fokus auf multidirektionale Rutschhemmung, angenehme Begehbarkeit und geringe Durchbiegung zwischen den Trägern.",
                  es: "Priorice agarre multidireccional, comodidad al caminar y control de flexión entre apoyos.",
                  fr: "Privilégiez l'adhérence multidirectionnelle, le confort de circulation et la limitation de la flèche entre appuis.",
                  pt: "Priorize tração multidirecional para pedestres, conforto ao caminhar e controle da deflexão entre apoios."
                }, lang)}
                </p>
              </div>

              <div className="p-4 rounded border border-border bg-muted/30">
                <span className="font-semibold text-foreground block mb-1">
                  {pick({
                  en: "Stair Treads & Running Boards",
                  zh: "楼梯踏步与登车脚踏",
                  de: "Treppenstufen & Trittleisten",
                  es: "Peldaños y estribos",
                  fr: "Marches d'escalier et marchepieds",
                  pt: "Degraus de escada e estribos"
                }, lang)}
                </span>
                <p className="text-muted-foreground leading-relaxed">
                  {pick({
                  en: "Concentrated edge impact requires reinforced nosings or high-traction profile shapes.",
                  zh: "前缘受力集中，需配合前沿折弯防滑护边或选用高摩擦齿形自锁孔型。",
                  de: "Punktuelle Kantenbelastung verlangt verstärkte Antrittskanten oder griffig gezahnte Lochungen.",
                  es: "El impacto en el borde requiere cantoneras reforzadas o perfiles de alta tracción mecánica.",
                  fr: "La charge concentrée sur le bord exige des nez de marche renforcés ou des profils crantés très adhérents.",
                  pt: "O impacto concentrado na borda requer testeiras reforçadas ou perfis denteados de alta tração."
                }, lang)}
                </p>
              </div>

              <div className="p-4 rounded border border-border bg-muted/30">
                <span className="font-semibold text-foreground block mb-1">
                  {pick({
                  en: "Vehicle Beds & Cargo Decks",
                  zh: "车辆底板与重型货台",
                  de: "Fahrzeugböden & Laderampen",
                  es: "Plataformas de carga y camiones",
                  fr: "Bennes et plates-formes de fret",
                  pt: "Caçambas de carga e carretas"
                }, lang)}
                </span>
                <p className="text-muted-foreground leading-relaxed">
                  {pick({
                  en: "Handles dynamic wheel loads, requiring heavier base gauge and dense under-structure support.",
                  zh: "承受叉车车轮或货物轮载滚动碾压，需要厚基板与高刚度骨架支撑。",
                  de: "Dynamische Radlasten erfordern stärkere Grundbleche und engmaschige Unterkonstruktionen.",
                  es: "Soporta cargas de ruedas y carretillas; exige mayor espesor base y vigas de soporte próximas.",
                  fr: "Supporte les charges roulantes dynamiques ; requiert une tôle de base plus épaisse et des supports rapprochés.",
                  pt: "Suporta cargas dinâmicas de rodas; exige espessura base maior e estrutura de apoio densa."
                }, lang)}
                </p>
              </div>
            </div>
          </div>

          {/* STEP 2: CHOOSE PATTERN */}
          <div className="border border-border rounded-lg p-6 sm:p-8 bg-card shadow-xs">
            <div className="flex items-start gap-4 mb-5">
              <div className="size-9 rounded-sm bg-primary text-primary-foreground flex items-center justify-center font-mono font-bold text-base shrink-0">
                2
              </div>
              <div>
                <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground block mb-0.5">
                  {pick({
                  en: "Step 2 · Choose the Pattern Profile",
                  zh: "步骤 2 · 匹配花纹形制与开孔类型",
                  de: "Schritt 2 · Riffelprofil & Lochungsart wählen",
                  es: "Paso 2 · Elegir el perfil del grabado o troquelado",
                  fr: "Étape 2 · Choisir le motif de larmage ou perforation",
                  pt: "Passo 2 · Escolher o padrão de relevo ou perfil perfurado"
                }, lang)}
                </span>
                <h3 className="text-xl sm:text-2xl font-bold text-foreground">
                  {pick({
                  en: "Compare Raised Lug Ribs vs. Punched Anti-Slip Sheets",
                  zh: "对比传统热轧凸纹与冲孔防滑板形制",
                  de: "Warmgewalzte Riffelungen vs. gestanzte Sicherheitsbleche vergleichen",
                  es: "Comparar relieves laminados frente a chapas troqueladas antideslizantes",
                  fr: "Comparer reliefs laminés et tôles perforées antidérapantes",
                  pt: "Comparar relevos laminados e chapas perfuradas antiderrapantes"
                }, lang)}
                </h3>
              </div>
            </div>

            <p className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-6">
              {pick({
              en: "Match the working surface to one of five profiles: classic rolled raised ribs (Diamond, Two-Bar, Five-Bar) provide solid containment; punched profile sheets (Fish-Eye, Crocodile Mouth) feature pierced openings designed for liquid evacuation and aggressive tooth mechanical lock.",
              zh: "根据防滑要求与现场排泄条件，在五种常见形制中进行选型：传统热轧凸纹板（菱形、两筋、五筋）适合实心地坪与封闭式载货面；冲孔防滑板（鱼眼孔、鳄鱼嘴）具有冲压通孔，专为液体导流、自洁排屑与重度油污抓地设计。",
              de: "Stimmen Sie die Lauffläche auf fünf Profile ab: Klassische warmgewalzte Riffelungen (Rauten, 2-Steg, 5-Steg) bieten geschlossene Tragflächen; gestanzte Profilbleche (Rundnoppen, Grip Strut) besitzen Durchbrüche zur Entwässerung und selbstreinigenden Schmutzabfuhr.",
              es: "Ajuste el uso a uno de cinco perfiles: los relieves laminados clásicos (diamante, dos barras, cinco barras) aportan una superficie cerrada; las chapas troqueladas (ojo de pez, boca de cocodrilo) incorporan orificios para drenaje y agarre mecánico dentado.",
              fr: "Adaptez la surface à cinq profils courants : les motifs laminés classiques (losange, 2 stries, 5 stries) offrent un plancher plein ; les tôles perforées profilées (emboutis ronds, crans dentelés) intègrent des perforations pour drainer les liquides et bloquer la glisse.",
              pt: "Combine a superfície de trabalho com um dos cinco perfis: os relevos laminados clássicos (diamante, duas barras, cinco barras) proporcionam retenção sólida; as chapas perfuradas repuxadas (olho de peixe, boca de jacaré) contam com aberturas vazadas para drenagem e tração denteada agressiva."
            }, lang)}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {patternProfiles.map((pattern, idx) => <div key={pattern.id} data-index={idx} className="rounded border border-border bg-card p-5 flex flex-col justify-between hover:border-primary/40 transition-colors">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between gap-2">
                      <Badge variant="outline" className="text-sm font-mono">
                        {pick(pattern.badge, lang)}
                      </Badge>
                      <span className="text-sm font-mono text-muted-foreground">
                        {pick(pattern.type, lang)}
                      </span>
                    </div>

                    <h4 className="text-lg font-bold text-foreground">
                      {pick(pattern.title, lang)}
                    </h4>

                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {pick(pattern.desc, lang)}
                    </p>

                    <div className="pt-2 border-t border-border space-y-1 text-sm">
                      <div className="text-muted-foreground">
                        <strong className="text-foreground">
                          {pick({
                        en: "Best for: ",
                        zh: "适用场景：",
                        de: "Ideal für: ",
                        es: "Ideal para: ",
                        fr: "Idéal pour : ",
                        pt: "Ideal para: "
                      }, lang)}
                        </strong>
                        {pick(pattern.bestFor, lang)}
                      </div>
                      <div className="text-muted-foreground font-mono">
                        {pick(pattern.thickness, lang)}
                      </div>
                      <div className="text-muted-foreground font-mono">
                        {pick(pattern.ribHeight, lang)}
                      </div>
                    </div>
                  </div>

                  <div className="pt-4 mt-4 border-t border-border">
                    <Button variant="outline" size="sm" asChild className="w-full justify-between">
                      <Link to={pattern.to}>
                        <span>
                          {pick({
                        en: `View ${pattern.id} details`,
                        zh: `查看${pick(pattern.title, "zh")}`,
                        de: `Details ansehen`,
                        es: `Ver detalles`,
                        fr: `Voir les détails`,
                        pt: `Ver detalhes`
                      }, lang)}
                        </span>
                        <ArrowRight className="size-3.5" />
                      </Link>
                    </Button>
                  </div>
                </div>)}
            </div>
          </div>

          {/* STEP 3: CHOOSE MATERIAL */}
          <div className="border border-border rounded-lg p-6 sm:p-8 bg-card shadow-xs">
            <div className="flex items-start gap-4 mb-5">
              <div className="size-9 rounded-sm bg-primary text-primary-foreground flex items-center justify-center font-mono font-bold text-base shrink-0">
                3
              </div>
              <div>
                <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground block mb-0.5">
                  {pick({
                  en: "Step 3 · Choose Base Material",
                  zh: "步骤 3 · 选择合金基材",
                  de: "Schritt 3 · Grundwerkstoff wählen",
                  es: "Paso 3 · Seleccionar material base",
                  fr: "Étape 3 · Choisir le matériau de base",
                  pt: "Passo 3 · Escolher o material base"
                }, lang)}
                </span>
                <h3 className="text-xl sm:text-2xl font-bold text-foreground">
                  {pick({
                  en: "Balance Environmental Corrosion, Dead Weight & Cost",
                  zh: "权衡耐腐蚀环境、结构自重与采购成本",
                  de: "Korrosionsbeständigkeit, Eigengewicht & Kosten abwägen",
                  es: "Equilibrar corrosión ambiental, peso propio y coste",
                  fr: "Équilibrer corrosion ambiante, poids propre et coût",
                  pt: "Equilibrar corrosão ambiental, peso próprio e custo"
                }, lang)}
                </h3>
              </div>
            </div>

            <p className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-6">
              {pick({
              en: "Select the base alloy according to ambient moisture and chemical exposure. Carbon steel provides economical high-load strength; stainless steel resists harsh washdown chemicals and saltwater; aluminum delivers high strength-to-weight ratio and natural atmospheric oxidation resistance.",
              zh: "根据现场腐蚀介质与环境湿度选择基材合金。碳素结构钢性价比高且结构强度优异；不锈钢专用于食品卫生、海水盐雾及强酸碱环境；铝合金质轻且天然防腐，适合移动运输设备与建筑装饰。",
              de: "Wählen Sie den Werkstoff entsprechend der Feuchte- und Chemikalienbelastung. Kohlenstoffstahl bietet wirtschaftliche Tragkraft; Edelstahl widersteht Reinigungsmedien und Salzwasser; Aluminium punktet mit geringem Gewicht und natürlicher Witterungsbeständigkeit.",
              es: "Seleccione la aleación según la humedad ambiental y la exposición química. El acero al carbono ofrece resistencia económica; el acero inoxidable soporta lavados químicos y salinidad; el aluminio proporciona ligereza y resistencia natural a la corrosión.",
              fr: "Sélectionnez l'alliage selon l'humidité ambiante et l'exposition chimique. L'acier au carbone assure une résistance économique élevée ; l'inox résiste aux lavages chimiques et à l'eau de mer ; l'aluminium offre un excellent ratio poids/résistance.",
              pt: "Selecione a liga metálica conforme a umidade e agentes químicos. O aço carbono oferece resistência estrutural econômica; o aço inoxidável resiste a lavagens químicas e névoa salina; o alumínio combina baixo peso com resistência à oxidação natural."
            }, lang)}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="p-5 rounded border border-border bg-card space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-bold text-foreground text-base">
                    {pick({
                    en: "Carbon Steel",
                    zh: "碳素结构钢 (Carbon Steel)",
                    de: "Kohlenstoffstahl",
                    es: "Acero al carbono",
                    fr: "Acier au carbone",
                    pt: "Aço carbono"
                  }, lang)}
                  </h4>
                  <Badge variant="secondary" className="font-mono text-sm">
                    Q235B / A36
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {pick({
                  en: "Most common alloy for heavy plant floors and stair treads. High weldability and stiffness. Requires hot-dip galvanizing for exterior moisture resistance.",
                  zh: "工业制造中最通用的钢板基材。具有优良的焊接性与结构承重力。室外使用必须配套热浸镀锌或长效防腐涂层。",
                  de: "Gängigster Werkstoff für Industrieböden und Treppenstufen. Hohe Steifigkeit und Schweißbarkeit. Benötigt Feuerverzinkung für dauerhaften Außeneinsatz.",
                  es: "El material más habitual en suelos industriales y peldaños. Gran soldabilidad y rigidez. Requiere galvanizado en caliente para exteriores.",
                  fr: "Nuance la plus courante pour planchers d'usine et marches d'escalier. Excellente soudabilité et rigidité. Nécessite une galvanisation à chaud en extérieur.",
                  pt: "Liga mais comum para pisos industriais e degraus de escada. Alta soldabilidade e rigidez. Requer galvanização a fogo para resistência externa à umidade."
                }, lang)}
                </p>
                <div className="pt-2 border-t border-border text-sm space-y-1">
                  <div>
                    <strong className="text-foreground">
                      {pick({
                      en: "Cost & Weight: ",
                      zh: "成本与自重：",
                      de: "Kosten & Gewicht: ",
                      es: "Coste y peso: ",
                      fr: "Coût et poids : ",
                      pt: "Custo e peso: "
                    }, lang)}
                    </strong>
                    <span className="text-muted-foreground">
                      {pick({
                      en: "Economical, highest mass",
                      zh: "成本经济，自重较高",
                      de: "Wirtschaftlich, höchstes Eigengewicht",
                      es: "Económico, mayor peso propio",
                      fr: "Économique, masse la plus élevée",
                      pt: "Econômico, maior massa própria"
                    }, lang)}
                    </span>
                  </div>
                  <div>
                    <strong className="text-foreground">
                      {pick({
                      en: "Typical Uses: ",
                      zh: "典型用途：",
                      de: "Typische Bereiche: ",
                      es: "Usos típicos: ",
                      fr: "Usages types : ",
                      pt: "Usos típicos: "
                    }, lang)}
                    </strong>
                    <span className="text-muted-foreground">
                      {pick({
                      en: "Plant platforms, truck beds",
                      zh: "厂房平台、重载踏步、车辆箱底",
                      de: "Werksbühnen, Lkw-Ladeflächen",
                      es: "Plataformas de nave, carrocerías",
                      fr: "Plates-formes d'atelier, bennes de camion",
                      pt: "Plataformas industriais, carrocerias de caminhão"
                    }, lang)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="p-5 rounded border border-border bg-card space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-bold text-foreground text-base">
                    {pick({
                    en: "Stainless Steel",
                    zh: "不锈钢 (Stainless Steel)",
                    de: "Edelstahl",
                    es: "Acero inoxidable",
                    fr: "Acier inoxydable",
                    pt: "Aço inoxidável"
                  }, lang)}
                  </h4>
                  <Badge variant="secondary" className="font-mono text-sm">
                    304 / 316
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {pick({
                  en: "Essential for food processing, chemical tanks, wastewater facilities, and marine installations. Grade 316 adds molybdenum for chloride and brine protection.",
                  zh: "食品加工车间、制药净化工程、滨海海洋工程及频繁高压化学清洗场所的选材。316材质更具备耐氯离子腐蚀特性。",
                  de: "Unverzichtbar für Lebensmittelverarbeitung, Chemieanlagen, Klärwerke und maritime Umgebungen. Güte 316 bietet Molybdänzusatz gegen Chloride.",
                  es: "Imprescindible en industria alimentaria, química, depuración y ambiente marino. El grado 316 añade molibdeno frente a cloruros.",
                  fr: "Indispensable pour l'agroalimentaire, la chimie, le traitement des eaux et le littoral. La nuance 316 offre une tenue accrue aux chlorures.",
                  pt: "Essencial para processamento de alimentos, indústrias químicas, saneamento e instalações marítimas. O grau 316 adiciona molibdênio para proteção contra cloretos."
                }, lang)}
                </p>
                <div className="pt-2 border-t border-border text-sm space-y-1">
                  <div>
                    <strong className="text-foreground">
                      {pick({
                      en: "Cost & Weight: ",
                      zh: "成本与自重：",
                      de: "Kosten & Gewicht: ",
                      es: "Coste y peso: ",
                      fr: "Coût et poids : ",
                      pt: "Custo e peso: "
                    }, lang)}
                    </strong>
                    <span className="text-muted-foreground">
                      {pick({
                      en: "Higher initial cost, long lifespan",
                      zh: "初始投入高，免维护周期长",
                      de: "Höhere Investition, lange Lebensdauer",
                      es: "Mayor coste inicial, vida útil prolongada",
                      fr: "Coût initial plus élevé, longue durée de vie",
                      pt: "Maior custo inicial, longa vida útil"
                    }, lang)}
                    </span>
                  </div>
                  <div>
                    <strong className="text-foreground">
                      {pick({
                      en: "Typical Uses: ",
                      zh: "典型用途：",
                      de: "Typische Bereiche: ",
                      es: "Usos típicos: ",
                      fr: "Usages types : ",
                      pt: "Usos típicos: "
                    }, lang)}
                    </strong>
                    <span className="text-muted-foreground">
                      {pick({
                      en: "Wash bays, chemical catwalks",
                      zh: "食品洗刷车间、化工步道、洁净室",
                      de: "Nassbereiche, Chemie-Laufstege",
                      es: "Zonas de lavado, pasarelas químicas",
                      fr: "Aires de lavage, passerelles chimiques",
                      pt: "Áreas de lavagem, passarelas em plantas químicas"
                    }, lang)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="p-5 rounded border border-border bg-card space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-bold text-foreground text-base">
                    {pick({
                    en: "Aluminum Alloy",
                    zh: "铝合金 (Aluminum Alloy)",
                    de: "Aluminiumlegierung",
                    es: "Aleación de aluminio",
                    fr: "Alliage d'aluminium",
                    pt: "Liga de alumínio"
                  }, lang)}
                  </h4>
                  <Badge variant="secondary" className="font-mono text-sm">
                    3003 / 5052 / 6061
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {pick({
                  en: "Approximately one-third the weight of steel. Non-sparking and non-magnetic. Excellent for vehicle weight reduction and architectural applications.",
                  zh: "约占钢板1/3自重，质轻且防爆无火花产生。耐大气腐蚀性能极佳，广泛应用于车载踏板、特种车厢及装饰地坪。",
                  de: "Rund ein Drittel des Stahlgewichts. Funkenfrei und amagnetisch. Ideal zur Gewichtseinsparung an Fahrzeugen und in der Architektur.",
                  es: "Aproximadamente un tercio del peso del acero. Antichispa y no magnético. Excelente para reducir peso en vehículos y arquitectura.",
                  fr: "Environ un tiers du poids de l'acier. Ne produit pas d'étincelles et amagnétique. Idéal pour l'allègement de véhicules et l'architecture.",
                  pt: "Aproximadamente um terço do peso do aço. Antifaiscante e não magnético. Excelente para redução de peso em veículos e acabamentos arquitetônicos."
                }, lang)}
                </p>
                <div className="pt-2 border-t border-border text-sm space-y-1">
                  <div>
                    <strong className="text-foreground">
                      {pick({
                      en: "Cost & Weight: ",
                      zh: "成本与自重：",
                      de: "Kosten & Gewicht: ",
                      es: "Coste y peso: ",
                      fr: "Coût et poids : ",
                      pt: "Custo e peso: "
                    }, lang)}
                    </strong>
                    <span className="text-muted-foreground">
                      {pick({
                      en: "Lightweight, corrosion resistant",
                      zh: "质轻便携，耐候性优异",
                      de: "Geringes Gewicht, korrosionsbeständig",
                      es: "Ligero y resistente a la intemperie",
                      fr: "Léger, résistant à la corrosion",
                      pt: "Leve e resistente à corrosão"
                    }, lang)}
                    </span>
                  </div>
                  <div>
                    <strong className="text-foreground">
                      {pick({
                      en: "Typical Uses: ",
                      zh: "典型用途：",
                      de: "Typische Bereiche: ",
                      es: "Usos típicos: ",
                      fr: "Usages types : ",
                      pt: "Usos típicos: "
                    }, lang)}
                    </strong>
                    <span className="text-muted-foreground">
                      {pick({
                      en: "Running boards, marine decks",
                      zh: "特种车厢、登车踏板、装饰步道",
                      de: "Trittbretter, Schiffsdecks",
                      es: "Estribos, cubiertas náuticas",
                      fr: "Marchepieds, ponts de bateaux",
                      pt: "Estribos de veículos, decks náuticos"
                    }, lang)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* STEP 4: CHOOSE THICKNESS & SHEET SIZE */}
          <div className="border border-border rounded-lg p-6 sm:p-8 bg-card shadow-xs">
            <div className="flex items-start gap-4 mb-5">
              <div className="size-9 rounded-sm bg-primary text-primary-foreground flex items-center justify-center font-mono font-bold text-base shrink-0">
                4
              </div>
              <div>
                <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground block mb-0.5">
                  {pick({
                  en: "Step 4 · Thickness & Sheet Sizing",
                  zh: "步骤 4 · 核定基板厚度与板材规格",
                  de: "Schritt 4 · Dicke & Plattenabmessungen",
                  es: "Paso 4 · Espesor de base y dimensiones",
                  fr: "Étape 4 · Épaisseur de base et dimensions",
                  pt: "Passo 4 · Espessura da base e dimensões das chapas"
                }, lang)}
                </span>
                <h3 className="text-xl sm:text-2xl font-bold text-foreground">
                  {pick({
                  en: "Verify Base Gauge by Span, Load & Sheet Dimensions",
                  zh: "根据跨距挠度与荷载要求确认基板厚度",
                  de: "Grundblechdicke nach Stützweite, Last & Abmessungen auslegen",
                  es: "Verificar espesor base según vano, carga y dimensiones",
                  fr: "Vérifier l'épaisseur de base selon portée, charge et format",
                  pt: "Verificar a espessura da base por vão, carga e dimensões da chapa"
                }, lang)}
                </h3>
              </div>
            </div>

            <p className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-6">
              {pick({
              en: "Structural load calculations rely strictly on base plate thickness (excluding lug height). Longer spans between support joists require thicker base gauges to prevent excessive deflection. Processing options can be coordinated through selected in-house production or partner factories, subject to project requirements and confirmation.",
              zh: "花纹钢板的结构承载力完全取决于基板净厚度（不含凸起纹高）。支撑梁或龙骨跨距越大，板厚需成比例增加以控制挠度。常用板材尺寸包括 1000×2000 mm、1219×2438 mm (4×8 ft)、1250×2500 mm 或 1500×3000 mm；加工方式可通过部分自有生产或合作工厂协调，视项目要求并经确认。",
              de: "Statische Lastberechnungen basieren ausschließlich auf der Grundblechdicke (ohne Riffelhöhe). Größere Stützweiten zwischen Trägern erfordern stärkere Bleche gegen unzulässige Durchbiegung. Bearbeitungsoptionen können über ausgewählte eigene Fertigung oder Partnerfabriken koordiniert werden, vorbehaltlich Projektanforderungen und Bestätigung.",
              es: "Los cálculos de carga estructural se basan estrictamente en el espesor de la chapa base (excluyendo la altura del resalte). Vanos más amplios exigen mayor calibre para evitar flexión excesiva. Las opciones de procesado se pueden coordinar mediante producción propia selecta o fábricas asociadas, según los requisitos del proyecto y previa confirmación.",
              fr: "Les calculs de charge structurale reposent strictement sur l'épaisseur de tôle de base (hors relief). Des portées plus longues entre supports requièrent des épaisseurs accrues pour limiter la flèche. Les options d'usinage peuvent être coordonnées via une production propre sélectionnée ou des usines partenaires, selon les exigences du projet et après confirmation.",
              pt: "Os cálculos estruturais dependem estritamente da espessura da chapa base (excluindo a altura do relevo). Vãos maiores entre apoios exigem espessuras mais robustas para evitar deflexão excessiva. As opções de processamento podem ser coordenadas por meio de produção própria selecionada ou fábricas parceiras, sujeitas aos requisitos do projeto e confirmação."
            }, lang)}
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="p-4 rounded border border-border bg-muted/20 space-y-3">
                <div className="flex items-center gap-2 text-foreground font-semibold text-sm">
                  <Ruler className="size-4 text-primary" />
                  <span>
                    {pick({
                    en: "Base Thickness Reference (Carbon Steel)",
                    zh: "基板厚度参考指导 (碳钢板)",
                    de: "Richtwerte Grundblechdicke (Kohlenstoffstahl)",
                    es: "Referencia de espesor base (acero al carbono)",
                    fr: "Référence d'épaisseur de base (acier au carbone)",
                    pt: "Referência de espessura da base (aço carbono)"
                  }, lang)}
                  </span>
                </div>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start justify-between gap-2 border-b border-border pb-1.5">
                    <span className="font-mono font-medium text-foreground">
                      1.5 mm – 2.5 mm
                    </span>
                    <span>
                      {pick({
                      en: "Light overlays, equipment covers, close-support walkways",
                      zh: "轻型贴面、机柜盖板、连续密集支撑步道",
                      de: "Leichte Verkleidungen, Abdeckungen, dicht unterstützte Stege",
                      es: "Revestimientos ligeros, tapas de equipos, pasarelas con apoyos continuos",
                      fr: "Habillages légers, capots de machines, passerelles à appuis rapprochés",
                      pt: "Revestimentos leves, tampas de máquinas, passarelas com apoio contínuo"
                    }, lang)}
                    </span>
                  </li>
                  <li className="flex items-start justify-between gap-2 border-b border-border pb-1.5">
                    <span className="font-mono font-medium text-foreground">
                      3.0 mm – 4.5 mm
                    </span>
                    <span>
                      {pick({
                      en: "Standard plant catwalks, stair treads, utility trailers",
                      zh: "标准工业人行走道、楼梯踏板、轻型货车底板",
                      de: "Standard-Laufstege, Treppenstufen, Nutzanhänger",
                      es: "Pasarelas industriales habituales, peldaños, remolques",
                      fr: "Passerelles industrielles courantes, marches, remorques",
                      pt: "Passarelas industriais padrão, degraus de escada, carretas leves"
                    }, lang)}
                    </span>
                  </li>
                  <li className="flex items-start justify-between gap-2 border-b border-border pb-1.5">
                    <span className="font-mono font-medium text-foreground">
                      5.0 mm – 8.0 mm
                    </span>
                    <span>
                      {pick({
                      en: "Heavy platforms, forklift aisles, wider span decking",
                      zh: "重载平台、中型叉车通行、大跨距过道",
                      de: "Schwerlastbühnen, Staplerverkehr, größere Spannweiten",
                      es: "Plataformas de alta carga, tráfico de carretillas, vanos amplios",
                      fr: "Plates-formes pour fortes charges, passage d'élévateurs, portées accrues",
                      pt: "Plataformas para carga pesada, tráfego de empilhadeiras, vãos mais largos"
                    }, lang)}
                    </span>
                  </li>
                  <li className="flex items-start justify-between gap-2">
                    <span className="font-mono font-medium text-foreground">
                      9.0 mm – 12.0 mm
                    </span>
                    <span>
                      {pick({
                      en: "Loading bays, heavy equipment floors, extreme point loads",
                      zh: "重型装卸码头、特种工程机械重载地坪",
                      de: "Ladezonen, Schwerlastböden, extreme Punktlasten",
                      es: "Muelles de carga, suelos para maquinaria pesada, cargas puntuales extremas",
                      fr: "Zones de chargement, sols pour engins lourds, charges ponctuelles extrêmes",
                      pt: "Docas de carga pesada, pisos de maquinário industrial, cargas pontuais extremas"
                    }, lang)}
                    </span>
                  </li>
                </ul>
              </div>

              <div className="p-4 rounded border border-border bg-card space-y-3">
                <div className="flex items-center gap-2 text-foreground font-semibold text-sm">
                  <Compass className="size-4 text-primary" />
                  <span>
                    {pick({
                    en: "Custom Cut-to-Size & Flatness",
                    zh: "定尺加工与板面平整度",
                    de: "Zuschnitt nach Maß & Ebenheit",
                    es: "Corte a medida y planicidad",
                    fr: "Découpe sur mesure et planéité",
                    pt: "Corte sob medida e planicidade"
                  }, lang)}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {pick({
                  en: "We provide cut-to-size shearing, laser contouring, edge notching, pre-punched fastener holes, and brake forming. Tolerances are confirmed for each project and producing factory.",
                  zh: "我们可提供开平定尺、激光切割异形、冲孔预留安装孔及折弯踢脚板加工。公差要求与板面平整度根据每批订单与生产工厂具体确认；可根据您的图纸提供配套加工协调。",
                  de: "Wir koordinieren Zuschnitte, Laserschneiden, Randklinkungen, vorgefertigte Befestigungslöcher und Abkantungen. Toleranzen werden projektbezogen und je Herstellerwerk bestätigt.",
                  es: "Facilitamos corte a medida, contorneado por láser, cajeados perimetrales, taladros de fijación y plegado. Las tolerancias se confirman según el proyecto y la fábrica productora.",
                  fr: "Nous coordonnons cisaillage sur mesure, découpe laser, grugeage, perçages de fixation et pliage. Les tolérances sont confirmées selon chaque projet et usine de production.",
                  pt: "Disponibilizamos corte sob medida, contorno a laser, chanfros laterais, furações pré-perfuradas e dobras. Tolerâncias são confirmadas para cada projeto e fábrica produtora."
                }, lang)}
                </p>
                <div className="text-sm text-muted-foreground font-mono bg-muted/40 p-2.5 rounded">
                  {pick({
                  en: "Standard sheet sizes may be available; availability is confirmed at quotation.",
                  zh: "标准板幅：1000×2000 mm / 1219×2438 mm / 1500×3000 mm；供货情况在报价时确认。",
                  de: "Standardformate (z. B. 1000×2000 mm, 1250×2500 mm, 1500×3000 mm) ggf. verfügbar; Verfügbarkeit wird bei Angebotserstellung bestätigt.",
                  es: "Los formatos estándar pueden estar disponibles; la disponibilidad se confirma en la cotización.",
                  fr: "Des formats de tôle standard peuvent être disponibles ; la disponibilité est confirmée au devis.",
                  pt: "Formatos padrão podem estar disponíveis; a disponibilidade é confirmada na cotação."
                }, lang)}
                </div>
              </div>
            </div>
          </div>

          {/* STEP 5: CHOOSE SURFACE FINISH */}
          <div className="border border-border rounded-lg p-6 sm:p-8 bg-card shadow-xs">
            <div className="flex items-start gap-4 mb-5">
              <div className="size-9 rounded-sm bg-primary text-primary-foreground flex items-center justify-center font-mono font-bold text-base shrink-0">
                5
              </div>
              <div>
                <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground block mb-0.5">
                  {pick({
                  en: "Step 5 · Choose Surface Finish",
                  zh: "步骤 5 · 表面处理工艺匹配",
                  de: "Schritt 5 · Oberflächenbehandlung festlegen",
                  es: "Paso 5 · Seleccionar acabado superficial",
                  fr: "Étape 5 · Définir la finition de surface",
                  pt: "Passo 5 · Escolher o acabamento superficial"
                }, lang)}
                </span>
                <h3 className="text-xl sm:text-2xl font-bold text-foreground">
                  {pick({
                  en: "Select Mill Finish, Industrial Paint, or Hot-Dip Galvanizing",
                  zh: "评估原面、防锈漆与热浸镀锌防护效果",
                  de: "Walzblank, Grundierung/Lack oder Feuerverzinkung wählen",
                  es: "Seleccionar acabado bruto, pintura industrial o galvanizado en caliente",
                  fr: "Choisir brut de laminage, peinture industrielle ou galvanisation à chaud",
                  pt: "Selecionar acabamento bruto, pintura industrial ou galvanização a fogo"
                }, lang)}
                </h3>
              </div>
            </div>

            <p className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-6">
              {pick({
              en: "Surface protection must match ambient atmospheric exposure. Hot-dip galvanizing produces a metallurgical zinc-iron alloy coating resistant to outdoor corrosion. Stainless steel is supplied mill pickled or satin, while aluminum is available mill finish or anodized.",
              zh: "根据使用环境的大气湿度与化学暴露等级确定表面涂层。热浸镀锌能为碳钢提供优良的电化学与物理屏障防护；不锈钢通常保留酸洗原面或根据要求进行拉丝钝化；铝合金可选用原色轧制或阳极氧化表面。",
              de: "Der Oberflächenschutz muss der atmosphärischen Beanspruchung entsprechen. Feuerverzinkung erzeugt eine zinkmetallurgische Schicht für dauerhafte Außenbewitterung. Edelstahl wird meist gebeizt oder satiniert geliefert, Aluminium walzblank oder eloxiert.",
              es: "La protección debe adecuarse a la exposición atmosférica. El galvanizado en caliente genera una capa metalúrgica de zinc resistente al exterior. El acero inoxidable se suministra decapado o satinado, y el aluminio en bruto o anodizado.",
              fr: "La protection doit correspondre à l'exposition atmosphérique. La galvanisation à chaud procure une barrière métallurgique durable pour l'extérieur. L'acier inoxydable est livré décapé ou satiné, et l'aluminium brut ou anodisé.",
              pt: "A proteção superficial deve corresponder à exposição atmosférica do local. A galvanização a fogo cria uma camada metalúrgica de zinco resistente a intempéries. O aço inoxidável é fornecido decapado ou escovado, enquanto o alumínio é disponibilizado em acabamento bruto ou anodizado."
            }, lang)}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div className="p-4 rounded border border-border bg-card space-y-2">
                <div className="flex items-center gap-2">
                  <Paintbrush className="size-4 text-primary" />
                  <h4 className="font-semibold text-foreground text-sm">
                    {pick({
                    en: "Mill Finish",
                    zh: "原面 / 轧制面 (Mill Finish)",
                    de: "Walzblank",
                    es: "Acabado bruto (Mill Finish)",
                    fr: "Brut de laminage",
                    pt: "Bruto de laminação (Mill Finish)"
                  }, lang)}
                  </h4>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {pick({
                  en: "Natural rolled mill surface. Suitable for indoor dry storage, dry machinery floors, or parts requiring subsequent welding and custom shop painting.",
                  zh: "热轧后保留出厂原生态表面。适用于室内绝对干燥环境，或由客户现场安装后统一喷漆、焊接总装。",
                  de: "Natürliche Walzoberfläche. Geeignet für trockene Innenräume, Maschinenverkleidungen oder Baugruppen, die nachträglich verschweißt und lackiert werden.",
                  es: "Superficie natural de laminación. Apta para interiores secos, bases de maquinaria o elementos que recibirán soldadura y pintura posterior.",
                  fr: "Surface naturelle issue du laminage. Convient aux stockages intérieurs secs ou aux pièces devant recevoir des soudures et une peinture d'atelier.",
                  pt: "Superfície de laminação natural. Indicada para áreas internas secas, bases de maquinário ou peças que passarão por solda e pintura posterior."
                }, lang)}
                </p>
                <Badge variant="outline" className="text-sm font-mono">
                  {pick({
                  en: "Indoor / Post-Fabrication",
                  zh: "室内 / 后续现场加工",
                  de: "Innenbereich / Weiterverarbeitung",
                  es: "Interior / Post-conformado",
                  fr: "Intérieur / Après-façonnage",
                  pt: "Uso interno / Pós-processamento"
                }, lang)}
                </Badge>
              </div>

              <div className="p-4 rounded border border-border bg-card space-y-2">
                <div className="flex items-center gap-2">
                  <Paintbrush className="size-4 text-primary" />
                  <h4 className="font-semibold text-foreground text-sm">
                    {pick({
                    en: "Shop Primer / Paint",
                    zh: "防锈底漆 / 喷漆 (Painted)",
                    de: "Grundierung / Lackierung",
                    es: "Imprimación / Pintura",
                    fr: "Primaire d'atelier / Peinture",
                    pt: "Primer anticorrosivo / Pintura"
                  }, lang)}
                  </h4>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {pick({
                  en: "Applies anti-corrosive primer or topcoat for visual identification and basic rust prevention. Requires scheduled maintenance touch-ups under high traffic.",
                  zh: "涂布环氧富锌底漆或防锈面漆，提供基础防锈屏障与定制标识色彩。受机械刮擦磨损部位需定期维护补漆。",
                  de: "Korrosionsschutz-Grundierung oder Decklack für Farbcodierung und Basisschutz. Bei hoher Trittbelastung sind regelmäßige Nachbesserungen erforderlich.",
                  es: "Aplica imprimación anticorrosiva o pintura de acabado para identificación y prevención básica. Requiere retoques ante tráfico intenso.",
                  fr: "Application d'un primaire antirouille ou d'une peinture pour repérage visuel et protection basique. Nécessite des retouches d'entretien en trafic dense.",
                  pt: "Aplicação de primer anticorrosivo ou tinta de acabamento para sinalização visual e proteção básica. Exige manutenção periódica sob tráfego intenso."
                }, lang)}
                </p>
                <Badge variant="outline" className="text-sm font-mono">
                  {pick({
                  en: "Visual ID / Controlled Indoor",
                  zh: "色彩标记 / 受控室内",
                  de: "Farbcodierung / Trockener Innenbereich",
                  es: "Identificación visual / Interior controlado",
                  fr: "Repérage visuel / Milieu intérieur contrôlé",
                  pt: "Identificação visual / Ambiente interno controlado"
                }, lang)}
                </Badge>
              </div>

              <div className="p-4 rounded border border-border bg-card space-y-2">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="size-4 text-chart-5" />
                  <h4 className="font-semibold text-foreground text-sm">
                    {pick({
                    en: "Hot-Dip Galvanized (HDG)",
                    zh: "热浸镀锌 (Hot-Dip Galvanized)",
                    de: "Feuerverzinkt (HDG)",
                    es: "Galvanizado en caliente (HDG)",
                    fr: "Galvanisé à chaud (HDG)",
                    pt: "Galvanizado a fogo (HDG)"
                  }, lang)}
                  </h4>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {pick({
                  en: "Hot-dip galvanizing per ASTM A123 / GB/T 13912 standard references delivers durable barrier and sacrificial protection for outdoor infrastructure.",
                  zh: "参照 ASTM A123 / GB/T 13912 等技术规范执行热浸镀锌处理，为碳钢提供长期户外大气防腐蚀保护。锌层厚度根据项目要求确认。",
                  de: "Feuerverzinkung gemäß Referenznormen wie ASTM A123 / GB/T 13912 bietet dauerhaften kathodischen Schutz im Freien. Zinkauflage wird projektbezogen bestätigt.",
                  es: "El galvanizado en caliente según normas de referencia como ASTM A123 / GB/T 13912 proporciona protección duradera en exteriores. Espesor confirmado por proyecto.",
                  fr: "La galvanisation à chaud selon les normes de référence ASTM A123 / GB/T 13912 assure une protection sacrificielle durable pour les infrastructures extérieures.",
                  pt: "A galvanização a fogo segundo normas de referência ASTM A123 / GB/T 13912 confere proteção catódica e de barreira duradoura em ambientes externos."
                }, lang)}
                </p>
                <Badge variant="secondary" className="text-sm font-mono">
                  {pick({
                  en: "Exterior Infrastructure",
                  zh: "户外严苛工况首选",
                  de: "Außeninfrastruktur",
                  es: "Infraestructuras exteriores",
                  fr: "Infrastructures extérieures",
                  pt: "Infraestrutura externa"
                }, lang)}
                </Badge>
              </div>
            </div>
          </div>

          {/* STEP 6: CONFIRM SLIP RESISTANCE & DOCS */}
          <div className="border border-border rounded-lg p-6 sm:p-8 bg-card shadow-xs">
            <div className="flex items-start gap-4 mb-5">
              <div className="size-9 rounded-sm bg-primary text-primary-foreground flex items-center justify-center font-mono font-bold text-base shrink-0">
                6
              </div>
              <div>
                <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground block mb-0.5">
                  {pick({
                  en: "Step 6 · Slip Needs & Documentation",
                  zh: "步骤 6 · 防滑性能与质量文件核验",
                  de: "Schritt 6 · Rutschhemmung & Prüfdokumentation",
                  es: "Paso 6 · Resistencia al deslizamiento y documentación",
                  fr: "Étape 6 · Adhérence et documentation qualité",
                  pt: "Passo 6 · Antiderrapância e documentação técnica"
                }, lang)}
                </span>
                <h3 className="text-xl sm:text-2xl font-bold text-foreground">
                  {pick({
                  en: "Confirm Slip Resistance & Request Material Documents",
                  zh: "防滑性能依项目核定，按需协调材质证明文件",
                  de: "Rutschhemmung projektbezogen abstimmen & Prüfzeugnisse anfordern",
                  es: "Confirmar resistencia al deslizamiento y solicitar certificados",
                  fr: "Confirmer l'adhérence et demander les certificats matière",
                  pt: "Confirmar resistência ao escorregamento e solicitar documentação de material"
                }, lang)}
                </h3>
              </div>
            </div>

            <p className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-6">
              {pick({
              en: "Slip resistance is confirmed per project and producing factory based on your specific floor conditions. Available processes, specifications, quality checks, and documentation are confirmed according to each product and project. Material test reports (MTR) and inspection records are available on request, depending on product and producing factory.",
              zh: "花纹钢板的防滑性能根据每个产品和项目的具体工况要求进行确认。我们避免对防滑等级做出不切实际的统称承诺。相关质量检验记录、材质证明书（MTR）及出厂质保证明可按需提供，具体取决于所选产品及生产工厂。",
              de: "Die Rutschhemmung wird projektbezogen und je nach Herstellerwerk anhand der realen Einsatzbedingungen bestätigt. Verfügbare Verfahren, Spezifikationen, Qualitätsprüfungen und Dokumentationen werden produkt- und projektspezifisch abgestimmt. Werkstoffprüfzeugnisse (MTR) und Prüfprotokolle sind auf Anfrage erhältlich, abhängig von Produkt und herstellendem Werk.",
              es: "La resistencia al deslizamiento se confirma según el proyecto y la fábrica productora en función de las condiciones reales del suelo. Los procesos disponibles, especificaciones, controles de calidad y documentación se confirman según cada producto y proyecto. Los informes de ensayo de material (MTR) y registros de inspección están disponibles a petición, según el producto y la fábrica productora.",
              fr: "La résistance au glissement est confirmée selon le projet et l'usine de production d'après vos conditions réelles d'utilisation. Les procédés disponibles, spécifications, contrôles qualité et documentations sont confirmés selon chaque produit et projet. Les certificats matière (MTR) et rapports d'inspection sont disponibles sur demande, selon le produit et l'usine productrice.",
              pt: "A resistência ao escorregamento é confirmada conforme o projeto e fábrica produtora, com base nas condições reais de serviço. Processos disponíveis, especificações, inspeções de qualidade e documentação são confirmados de acordo com cada produto e projeto. Certificados de material (MTR) e relatórios de inspeção estão disponíveis mediante solicitação, dependendo do produto e da fábrica produtora."
            }, lang)}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="p-4 rounded border border-border bg-muted/40 flex items-start gap-3">
                <Footprints className="size-5 text-primary shrink-0 mt-0.5" />
                <div className="space-y-1 text-muted-foreground">
                  <strong className="text-foreground block">
                    {pick({
                    en: "Slip Resistance Confirmed per Project",
                    zh: "防滑要求依工况确认",
                    de: "Rutschhemmung projektbezogen bestätigt",
                    es: "Resistencia al deslizamiento confirmada según el proyecto",
                    fr: "Adhérence confirmée selon le projet",
                    pt: "Resistência ao escorregamento confirmada conforme o projeto"
                  }, lang)}
                  </strong>
                  <span>
                    {pick({
                    en: "For sloped walkways, wet wash bays, or oily environments, share your incline and floor conditions; our team will recommend appropriate raised lug or punched profiles.",
                    zh: "对于倾斜坡道、重油污或积水车间，建议提供现场倾斜角度与工作介质；我们的团队将协助匹配适宜的花纹凸棱或冲孔开孔形制。",
                    de: "Für Rampen, Waschbereiche oder ölige Arbeitszonen teilen Sie bitte Neigung und Belastungsart mit; unser Team empfiehlt geeignete Riffelungen oder Lochprofile.",
                    es: "En rampas inclinadas, zonas húmedas o entornos con aceite, indique la pendiente y el fluido presente; nuestro equipo le recomendará el perfil más adecuado.",
                    fr: "Pour les rampes inclinées, aires humides ou huileuses, précisez la pente et la nature des fluides ; notre équipe vous orientera vers le profil larmé ou perforé approprié.",
                    pt: "Para rampas inclinadas, áreas de lavagem ou locais com acúmulo de óleo, informe a inclinação e as condições do piso; nossa equipe recomendará o relevo ou perfil perfurado mais adequado."
                  }, lang)}
                  </span>
                </div>
              </div>

              <div className="p-4 rounded border border-border bg-muted/40 flex items-start gap-3">
                <FileCheck2 className="size-5 text-chart-5 shrink-0 mt-0.5" />
                <div className="space-y-1 text-muted-foreground">
                  <strong className="text-foreground block">
                    {pick({
                    en: "Quality Documentation on Request",
                    zh: "技术文件按需协调",
                    de: "Qualitätsdokumentation auf Anfrage",
                    es: "Documentación de calidad a petición",
                    fr: "Documentation qualité sur demande",
                    pt: "Documentação de qualidade sob consulta"
                  }, lang)}
                  </strong>
                  <span>
                    {pick({
                    en: "Mill test certificates (MTR), galvanizing coating weight verification, and dimensional inspection records are available on request, depending on product and producing factory.",
                    zh: "材质证明书（MTR）、镀锌层附着量检测记录及尺寸公差检验报告可根据项目要求协调提供，具体取决于产品与生产工厂。",
                    de: "Werkstoffprüfzeugnisse (MTR), Nachweise zum Zinkauflagegewicht und Maßkontrollberichte können je nach Produkt und Herstellerwerk auf Anfrage bereitgestellt werden.",
                    es: "Los informes de ensayo del material (MTR), registros de espesor de zinc y comprobaciones dimensionales pueden coordinarse según el producto y la fábrica.",
                    fr: "Les certificats matière (MTR), contrôles d'épaisseur de galvanisation et rapports dimensionnels peuvent être coordonnés sur demande, selon le produit et l'usine.",
                    pt: "Certificados de material (MTR), relatórios de camada de galvanização e medições dimensionais podem ser coordenados mediante solicitação, conforme o produto e a fábrica."
                  }, lang)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* COMPACT MATRIX TABLE: USE CASE -> SUGGESTED PATTERN */}
        <div className="mt-16 sm:mt-20">
          <div className="mb-6 border-l-[3px] border-accent pl-3">
            <Badge variant="outline" className="mb-2 font-mono text-xs uppercase tracking-wider">
              {pick({
              en: "Quick Reference Matrix",
              zh: "快速速查表",
              de: "Schnellübersicht",
              es: "Matriz de referencia rápida",
              fr: "Matrice de référence rapide",
              pt: "Matriz de consulta rápida"
            }, lang)}
            </Badge>
            <h3 className="text-2xl sm:text-3xl font-bold font-serif text-foreground">
              {pick({
              en: "Application vs. Suggested Checkered Plate Profile",
              zh: "常见应用场景与推荐花纹匹配表",
              de: "Anwendungsbereiche und empfohlene Riffelblechprofile",
              es: "Aplicaciones frente a perfiles sugeridos de chapa lagrimada",
              fr: "Applications et profils de tôle larmée suggérés",
              pt: "Aplicações versus perfis recomendados de chapa xadrez"
            }, lang)}
            </h3>
            <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
              {pick({
              en: "Quick reference guide matching service conditions to profile shapes and alloy selections.",
              zh: "根据典型工况环境快速对照推荐板型、替代方案与材质建议。",
              de: "Kurzübersicht zur Abstimmung von Umgebungsbedingungen auf Profile und Werkstoffgüten.",
              es: "Guía rápida para relacionar las condiciones de servicio con los perfiles y aleaciones.",
              fr: "Guide de synthèse associant conditions de service, formes de profil et nuances d'alliage.",
              pt: "Guia de consulta rápida relacionando condições de uso, padrões de relevo e ligas recomendadas."
            }, lang)}
            </p>
          </div>

          {/* Mobile / Tablet cards view (< lg) */}
          <div className="lg:hidden space-y-4">
            {useCaseMatrix.map((row, idx) => <div key={idx} data-index={idx} className="rounded-md border border-border bg-card p-4 space-y-3">
                <div>
                  <span className="text-sm font-semibold uppercase tracking-wider text-muted-foreground block mb-1">
                    {pick({
                  en: "Application & Exposure",
                  zh: "应用场景与工况",
                  de: "Einsatzbereich & Belastung",
                  es: "Aplicación y condiciones",
                  fr: "Application et environnement",
                  pt: "Aplicação e ambiente"
                }, lang)}
                  </span>
                  <p className="text-sm font-medium text-foreground whitespace-normal break-words leading-relaxed">
                    {pick(row.application, lang)}
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-border">
                  <div>
                    <span className="text-sm font-semibold uppercase tracking-wider text-muted-foreground block mb-1">
                      {pick({
                    en: "Suggested Profile",
                    zh: "推荐板面形制",
                    de: "Empfohlenes Profil",
                    es: "Perfil sugerido",
                    fr: "Profil suggéré",
                    pt: "Perfil sugerido"
                  }, lang)}
                    </span>
                    <p className="text-sm font-semibold text-primary whitespace-normal break-words">
                      {pick(row.suggested, lang)}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm font-semibold uppercase tracking-wider text-muted-foreground block mb-1">
                      {pick({
                    en: "Recommended Alloy",
                    zh: "推荐材质",
                    de: "Empfohlener Werkstoff",
                    es: "Material recomendado",
                    fr: "Matériau recommandé",
                    pt: "Material recomendado"
                  }, lang)}
                    </span>
                    <p className="text-sm text-foreground whitespace-normal break-words">
                      {pick(row.material, lang)}
                    </p>
                  </div>
                </div>

                <div className="pt-2 border-t border-border">
                  <span className="text-sm font-semibold uppercase tracking-wider text-muted-foreground block mb-1">
                    {pick({
                  en: "Alternative / Note",
                  zh: "工程备选 / 注意要点",
                  de: "Alternative / Hinweis",
                  es: "Alternativa / Observación",
                  fr: "Alternative / Remarque",
                  pt: "Alternativa / Observação"
                }, lang)}
                  </span>
                  <p className="text-sm text-muted-foreground whitespace-normal break-words leading-relaxed">
                    {pick(row.altNote, lang)}
                  </p>
                </div>
              </div>)}
          </div>

          {/* Desktop table view (>= lg) */}
          <div className="hidden lg:block rounded-md border border-border bg-card overflow-hidden shadow-xs">
            <Table className="table-fixed w-full">
              <TableHeader className="bg-muted/70">
                <TableRow className="border-b border-border">
                  <TableHead className="w-[28%] px-4 py-3.5 text-left align-top text-sm font-semibold uppercase tracking-wider text-foreground whitespace-normal break-words">
                    {pick({
                    en: "Application & Exposure",
                    zh: "应用场景与工况",
                    de: "Einsatzbereich & Belastung",
                    es: "Aplicación y condiciones",
                    fr: "Application et environnement",
                    pt: "Aplicação e ambiente"
                  }, lang)}
                  </TableHead>
                  <TableHead className="w-[28%] px-4 py-3.5 text-left align-top text-sm font-semibold uppercase tracking-wider text-foreground whitespace-normal break-words">
                    {pick({
                    en: "Suggested Profile",
                    zh: "推荐板面形制",
                    de: "Empfohlenes Profil",
                    es: "Perfil sugerido",
                    fr: "Profil suggéré",
                    pt: "Perfil sugerido"
                  }, lang)}
                  </TableHead>
                  <TableHead className="w-[24%] px-4 py-3.5 text-left align-top text-sm font-semibold uppercase tracking-wider text-foreground whitespace-normal break-words">
                    {pick({
                    en: "Recommended Alloy",
                    zh: "推荐材质",
                    de: "Empfohlener Werkstoff",
                    es: "Material recomendado",
                    fr: "Matériau recommandé",
                    pt: "Material recomendado"
                  }, lang)}
                  </TableHead>
                  <TableHead className="w-[20%] px-4 py-3.5 text-left align-top text-sm font-semibold uppercase tracking-wider text-foreground whitespace-normal break-words">
                    {pick({
                    en: "Alternative / Note",
                    zh: "工程备选 / 注意要点",
                    de: "Alternative / Hinweis",
                    es: "Alternativa / Observación",
                    fr: "Alternative / Remarque",
                    pt: "Alternativa / Observação"
                  }, lang)}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {useCaseMatrix.map((row, idx) => {
                const isEven = idx % 2 === 1;
                return <TableRow key={idx} data-index={idx} className={`border-b border-border last:border-b-0 hover:bg-muted/60 transition-colors ${isEven ? "bg-muted/40" : "bg-card"}`}>
                      <TableCell className="px-4 py-3.5 align-top font-medium text-foreground text-sm whitespace-normal break-words">
                        {pick(row.application, lang)}
                      </TableCell>
                      <TableCell className="px-4 py-3.5 align-top text-sm font-semibold text-primary whitespace-normal break-words">
                        {pick(row.suggested, lang)}
                      </TableCell>
                      <TableCell className="px-4 py-3.5 align-top text-sm text-foreground whitespace-normal break-words">
                        {pick(row.material, lang)}
                      </TableCell>
                      <TableCell className="px-4 py-3.5 align-top text-sm text-muted-foreground whitespace-normal break-words leading-relaxed">
                        {pick(row.altNote, lang)}
                      </TableCell>
                    </TableRow>;
              })}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* COMMON MISTAKES ACCORDION / LIST */}
        <div className="mt-16 sm:mt-20">
          <div className="mb-6 flex items-center gap-3 border-l-[3px] border-accent pl-3">
            <div>
              <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground block">
                {pick({
                en: "Pitfalls & Best Practices",
                zh: "选型防坑指南",
                de: "Häufige Fehler & Best Practices",
                es: "Errores comunes y buenas prácticas",
                fr: "Pièges fréquents et bonnes pratiques",
                pt: "Erros comuns e boas práticas"
              }, lang)}
              </span>
              <h3 className="text-2xl sm:text-3xl font-bold font-serif text-foreground">
                {pick({
                en: "Common Checkered Plate Selection Mistakes",
                zh: "花纹钢板常见选型误区",
                de: "Häufige Fehler bei der Riffelblechauswahl",
                es: "Errores habituales al elegir chapa lagrimada",
                fr: "Erreurs courantes lors du choix d'une tôle larmée",
                pt: "Erros comuns na especificação de chapa xadrez"
              }, lang)}
              </h3>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {commonMistakes.map((item, idx) => <Card key={idx} data-index={idx} className="border-border bg-card shadow-xs">
                <CardHeader className="pb-2">
                  <div className="flex items-start gap-2.5">
                    <TriangleAlert className="size-4 text-accent shrink-0 mt-0.5" />
                    <CardTitle className="text-base font-bold text-foreground">
                      {pick(item.mistake, lang)}
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="pt-1">
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {pick(item.desc, lang)}
                  </p>
                </CardContent>
              </Card>)}
          </div>
        </div>

        {/* RELATED GUIDES & SPECIFICATIONS SECTION */}
        <div className="mt-16 sm:mt-20 pt-12 border-t border-border">
          <div className="mb-6 border-l-[3px] border-accent pl-3">
            <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground block mb-1">
              {pick({
              en: "Related Resources & Specifications",
              zh: "关联技术资源与产品",
              de: "Verwandte Ressourcen & Spezifikationen",
              es: "Recursos relacionados y especificaciones",
              fr: "Ressources et spécifications associées",
              pt: "Recursos relacionados e especificações"
            }, lang)}
            </span>
            <h3 className="text-xl sm:text-2xl font-bold font-serif text-foreground">
              {pick({
              en: "Explore Related Specifications and Products",
              zh: "查阅相关规格表与技术指南",
              de: "Verwandte Datenblätter und Produktreihen entdecken",
              es: "Explorar especificaciones y productos relacionados",
              fr: "Explorer les spécifications et produits associés",
              pt: "Explorar especificações e produtos relacionados"
            }, lang)}
            </h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="p-4 rounded border border-border bg-card hover:border-primary/40 transition-colors flex flex-col justify-between">
              <div>
                <h4 className="font-semibold text-foreground text-sm mb-1">
                  {pick({
                  en: "Checkered Plate Overview",
                  zh: "花纹钢板全系列",
                  de: "Riffelblech-Übersicht",
                  es: "Visión general de chapa lagrimada",
                  fr: "Vue d'ensemble des tôles larmées",
                  pt: "Visão geral de chapa xadrez"
                }, lang)}
                </h4>
                <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                  {pick({
                  en: "Browse our complete range of rolled checkered and punched profile plates.",
                  zh: "浏览热轧花纹板与冲孔防滑板完整产品系列及加工协调能力。",
                  de: "Komplettes Spektrum an warmgewalzten Riffelblechen und gestanzten Sicherheitsblechen.",
                  es: "Explore la gama completa de chapas lagrimadas laminadas y chapas troqueladas antideslizantes.",
                  fr: "Parcourez notre gamme complète de tôles larmées laminées et de tôles perforées profilées.",
                  pt: "Conheça a linha completa de chapas xadrez laminadas e chapas perfiladas antiderrapantes."
                }, lang)}
                </p>
              </div>
              <Button variant="outline" size="sm" asChild className="w-full">
                <Link to="/CheckeredPlatePage">
                  <span>
                    {pick({
                    en: "Checkered Plate",
                    zh: "查看花纹钢板",
                    de: "Riffelblech ansehen",
                    es: "Ver chapa lagrimada",
                    fr: "Voir la tôle larmée",
                    pt: "Ver chapa xadrez"
                  }, lang)}
                  </span>
                  <ArrowRight className="size-3.5 ml-1" />
                </Link>
              </Button>
            </div>

            <div className="p-4 rounded border border-border bg-card hover:border-primary/40 transition-colors flex flex-col justify-between">
              <div>
                <h4 className="font-semibold text-foreground text-sm mb-1">
                  {pick({
                  en: "Checkered Plate Specifications",
                  zh: "产品技术规格表",
                  de: "Riffelblech-Spezifikationen",
                  es: "Especificaciones de chapa lagrimada",
                  fr: "Spécifications des tôles larmées",
                  pt: "Especificações de chapa xadrez"
                }, lang)}
                </h4>
                <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                  {pick({
                  en: "Detailed gauge tables, pattern heights, and reference standards across products.",
                  zh: "查阅完整的标准尺寸、基板厚度、花纹高度及参考技术标准。",
                  de: "Detaillierte Blechstärkentabellen, Riffelhöhen und Referenznormen für alle Ausführungen.",
                  es: "Tablas detalladas de calibres, alturas de relieve y normas técnicas de referencia.",
                  fr: "Tableaux de calibres, hauteurs de relief et normes de référence pour tous nos produits.",
                  pt: "Tabelas completas de espessuras, alturas de relevo e normas de referência aplicáveis."
                }, lang)}
                </p>
              </div>
              <Button variant="outline" size="sm" asChild className="w-full">
                <Link to="/ProductSpecificationsPage">
                  <span>
                    {pick({
                    en: "All Specifications",
                    zh: "产品技术规格",
                    de: "Alle Spezifikationen",
                    es: "Todas las especificaciones",
                    fr: "Toutes les spécifications",
                    pt: "Todas as especificações"
                  }, lang)}
                  </span>
                  <ArrowRight className="size-3.5 ml-1" />
                </Link>
              </Button>
            </div>

            <div className="p-4 rounded border border-border bg-card hover:border-primary/40 transition-colors flex flex-col justify-between">
              <div>
                <h4 className="font-semibold text-foreground text-sm mb-1">
                  {pick({
                  en: "Bar Grating (Open Decking)",
                  zh: "钢格板产品系列",
                  de: "Gitterrost (Offene Roste)",
                  es: "Rejilla de barras (piso abierto)",
                  fr: "Caillebotis (plancher ajouré)",
                  pt: "Grelha de barras (piso vazado)"
                }, lang)}
                </h4>
                <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                  {pick({
                  en: "Open-mesh alternative for long spans, heavy drainage, and light airflow requirements.",
                  zh: "室外大跨距走道或需自然排液通风地坪的理想替代选型。",
                  de: "Offene Alternative für große Stützweiten, optimalen Ablauf und freie Luftzirkulation.",
                  es: "Alternativa abierta para vanos amplios, alto drenaje y paso de luz o ventilación.",
                  fr: "Alternative ajourée pour grandes portées, drainage élevé et passage de l'air.",
                  pt: "Alternativa aberta para grandes vãos, máxima drenagem de líquidos e passagem de ar."
                }, lang)}
                </p>
              </div>
              <Button variant="outline" size="sm" asChild className="w-full">
                <Link to="/BarGratingPage">
                  <span>
                    {pick({
                    en: "Bar Grating",
                    zh: "查看钢格板",
                    de: "Gitterrost ansehen",
                    es: "Ver rejilla de barras",
                    fr: "Voir le caillebotis",
                    pt: "Ver grelha de barras"
                  }, lang)}
                  </span>
                  <ArrowRight className="size-3.5 ml-1" />
                </Link>
              </Button>
            </div>

            <div className="p-4 rounded border border-border bg-card hover:border-primary/40 transition-colors flex flex-col justify-between">
              <div>
                <h4 className="font-semibold text-foreground text-sm mb-1">
                  {pick({
                  en: "Materials & Finishes",
                  zh: "材质与表面处理",
                  de: "Werkstoffe & Oberflächen",
                  es: "Materiales y acabados",
                  fr: "Matériaux et finitions",
                  pt: "Materiais e acabamentos"
                }, lang)}
                </h4>
                <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                  {pick({
                  en: "Technical details on carbon steel, stainless steel, aluminum, and protective coatings.",
                  zh: "详细查阅碳钢、不锈钢、铝合金及热浸镀锌防腐涂层技术说明。",
                  de: "Technische Details zu Kohlenstoffstahl, Edelstahl, Aluminium und Schutzbeschichtungen.",
                  es: "Detalles técnicos sobre acero al carbono, acero inoxidable, aluminio y recubrimientos.",
                  fr: "Détails techniques sur l'acier au carbone, l'inox, l'aluminium et les revêtements protecteurs.",
                  pt: "Detalhes técnicos sobre aço carbono, aço inoxidável, alumínio e revestimentos protetores."
                }, lang)}
                </p>
              </div>
              <Button variant="outline" size="sm" asChild className="w-full">
                <Link to="/MaterialsFinishesPage">
                  <span>
                    {pick({
                    en: "Materials & Finishes",
                    zh: "材质与表面处理",
                    de: "Werkstoffe & Oberflächen",
                    es: "Materiales y acabados",
                    fr: "Matériaux et finitions",
                    pt: "Materiais e acabamentos"
                  }, lang)}
                  </span>
                  <ArrowRight className="size-3.5 ml-1" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}